import { PythonTopicExercise } from '../../../../types/python-topic';

export const clasesObjetosExercises: PythonTopicExercise[] = [
  // ========== CLASES Y MÉTODOS (1-10) ==========
  {
    id: 1,
    title: 'Crear tu primera clase',
    description: 'Aprende a definir una clase básica en Python y crear instancias de ella.',
    initialCode: `# Define una clase llamada Persona
class Persona:
    def __init__(self, nombre, edad):
        # Completa: asigna nombre y edad a self
        pass
    
    def presentarse(self):
        # Completa: devuelve un string "Soy {nombre} y tengo {edad} años"
        pass

# Crea una instancia de Persona
persona1 = Persona("Ana", 25)
print(persona1.presentarse())`,
    solution: `# Define una clase llamada Persona
class Persona:
    def __init__(self, nombre, edad):
        # Completa: asigna nombre y edad a self
        self.nombre = nombre
        self.edad = edad
    
    def presentarse(self):
        # Completa: devuelve un string "Soy {nombre} y tengo {edad} años"
        return f"Soy {self.nombre} y tengo {self.edad} años"

# Crea una instancia de Persona
persona1 = Persona("Ana", 25)
print(persona1.presentarse())`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Crear persona y presentarse',
        input: {},
        expectedOutput: 'Soy Ana y tengo 25 años',
        description: 'Verifica que la clase Persona funcione correctamente'
      }
    ],
    hints: [
      'Usa class para definir una clase',
      '__init__ es el constructor que inicializa el objeto',
      'self es el primer parámetro de todos los métodos',
      'self.nombre y self.edad son atributos de la instancia'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué aprender clases?',
      content: `Las **clases** son la base de la programación orientada a objetos. Te permiten agrupar datos y funciones relacionadas en un solo concepto.

### ¿Por qué son esenciales?
- **Organización**: Agrupa datos y comportamiento relacionados
- **Reutilización**: Crea múltiples objetos del mismo tipo
- **Abstracción**: Oculta detalles de implementación
- **Mantenibilidad**: Código más organizado y fácil de mantener

### En el mundo real:
Cuando un sistema necesita manejar múltiples usuarios, crea una clase Usuario con atributos (nombre, email) y métodos (login, logout). Sin clases, tendrías que manejar cada usuario por separado.`,
      keyPoints: [
        'class define una nueva clase',
        '__init__ inicializa el objeto cuando se crea',
        'self referencia a la instancia actual',
        'Los métodos son funciones dentro de la clase'
      ],
      realWorldExample: 'En un sistema de e-commerce, la clase Producto agrupa nombre, precio y stock junto con métodos como actualizar_precio() y verificar_stock().'
    },
    theory: `# Clases en Python

## ¿Qué es una Clase?

Una **clase** es una plantilla para crear objetos. Define qué atributos (datos) y métodos (comportamiento) tendrán los objetos creados a partir de ella.

## Sintaxis Básica

\`\`\`python
class Persona:
    def __init__(self, nombre, edad):
        self.nombre = nombre
        self.edad = edad
    
    def presentarse(self):
        return f"Soy {self.nombre} y tengo {self.edad} años"
\`\`\`

## Componentes de una Clase

### 1. Definición de Clase
\`\`\`python
class Persona:
\`\`\`
- \`class\`: Palabra clave para definir una clase
- \`Persona\`: Nombre de la clase (convención: PascalCase)

### 2. Constructor (__init__)
\`\`\`python
def __init__(self, nombre, edad):
    self.nombre = nombre
    self.edad = edad
\`\`\`
- \`__init__\`: Método especial que se ejecuta al crear un objeto
- \`self\`: Referencia a la instancia actual
- Inicializa los atributos del objeto

### 3. Métodos
\`\`\`python
def presentarse(self):
    return f"Soy {self.nombre} y tengo {self.edad} años"
\`\`\`
- Funciones definidas dentro de la clase
- Acceden a atributos con \`self.atributo\`

### 4. Crear Instancias
\`\`\`python
persona1 = Persona("Ana", 25)
\`\`\`
- Llama a la clase como si fuera una función
- Pasa argumentos al constructor

## Ventajas

- **Encapsulación**: Datos y métodos juntos
- **Reutilización**: Múltiples objetos de la misma clase
- **Organización**: Código más estructurado`,
    tips: [
      'Siempre usa self como primer parámetro en métodos',
      'Los nombres de clases deben ser en PascalCase',
      '__init__ se ejecuta automáticamente al crear un objeto',
      'self.nombre crea un atributo de la instancia',
      'Los métodos pueden acceder a atributos con self'
    ],
    advice: [
      'Practica creando clases simples primero',
      'Asegúrate de entender qué es self',
      'Experimenta creando múltiples instancias',
      'Revisa cómo se usan las clases en código existente'
    ]
  },
  {
    id: 2,
    title: 'Atributos de instancia',
    description: 'Aprende a trabajar con atributos de instancia y cómo acceder a ellos.',
    initialCode: `# Define una clase CuentaBancaria
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial):
        # Completa: inicializa titular y saldo
        pass
    
    def consultar_saldo(self):
        # Completa: devuelve el saldo actual
        pass

# Crea una cuenta y consulta el saldo
cuenta = CuentaBancaria("Juan", 1000)
print(f"Saldo: {cuenta.consultar_saldo()}")`,
    solution: `# Define una clase CuentaBancaria
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial):
        # Completa: inicializa titular y saldo
        self.titular = titular
        self.saldo = saldo_inicial
    
    def consultar_saldo(self):
        # Completa: devuelve el saldo actual
        return self.saldo

# Crea una cuenta y consulta el saldo
cuenta = CuentaBancaria("Juan", 1000)
print(f"Saldo: {cuenta.consultar_saldo()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Consultar saldo',
        input: {},
        expectedOutput: 'Saldo: 1000',
        description: 'Verifica que los atributos se inicialicen correctamente'
      }
    ],
    hints: [
      'self.titular crea un atributo llamado titular',
      'Los atributos se inicializan en __init__',
      'Puedes acceder a atributos con self.atributo',
      'Cada instancia tiene sus propios atributos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar atributos de instancia?',
      content: `Los **atributos de instancia** almacenan datos específicos de cada objeto. Cada instancia tiene sus propios valores independientes.

### ¿Por qué son importantes?
- **Datos únicos**: Cada objeto tiene sus propios valores
- **Persistencia**: Los datos se mantienen mientras el objeto existe
- **Acceso directo**: Puedes acceder a atributos desde métodos
- **Encapsulación**: Los datos están dentro del objeto

### En el mundo real:
En un sistema bancario, cada cuenta tiene su propio titular y saldo. Sin atributos de instancia, todas las cuentas compartirían los mismos valores.`,
      keyPoints: [
        'Los atributos se definen con self.nombre',
        'Cada instancia tiene sus propios atributos',
        'Los atributos se inicializan en __init__',
        'Puedes acceder a atributos desde cualquier método'
      ],
      realWorldExample: 'En un sistema de gestión de empleados, cada empleado tiene su propio nombre, salario y departamento almacenados como atributos de instancia.'
    },
    theory: `# Atributos de Instancia

## ¿Qué son los Atributos de Instancia?

Los **atributos de instancia** son variables que pertenecen a una instancia específica de una clase. Cada objeto tiene sus propios valores.

## Sintaxis

\`\`\`python
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial):
        self.titular = titular      # Atributo de instancia
        self.saldo = saldo_inicial  # Atributo de instancia
\`\`\`

## Características

### 1. Inicialización
Los atributos se inicializan en \`__init__\`:
\`\`\`python
self.titular = titular
\`\`\`

### 2. Acceso
Puedes acceder a atributos desde métodos:
\`\`\`python
def consultar_saldo(self):
    return self.saldo
\`\`\`

### 3. Independencia
Cada instancia tiene sus propios atributos:
\`\`\`python
cuenta1 = CuentaBancaria("Juan", 1000)
cuenta2 = CuentaBancaria("María", 500)
# cuenta1.saldo es independiente de cuenta2.saldo
\`\`\`

## Ventajas

- **Aislamiento**: Cada objeto tiene sus propios datos
- **Persistencia**: Los datos se mantienen durante la vida del objeto
- **Flexibilidad**: Puedes tener múltiples objetos con diferentes valores`,
    tips: [
      'Siempre inicializa atributos en __init__',
      'Usa self.nombre para crear atributos',
      'Los atributos son accesibles desde cualquier método',
      'Cada instancia tiene sus propios atributos',
      'Puedes acceder a atributos con objeto.atributo'
    ],
    advice: [
      'Practica creando clases con múltiples atributos',
      'Experimenta accediendo a atributos desde métodos',
      'Crea múltiples instancias y observa que tienen valores independientes',
      'Aprende a distinguir entre atributos de instancia y variables locales'
    ]
  },
  {
    id: 3,
    title: 'Métodos con parámetros',
    description: 'Aprende a crear métodos que reciben parámetros adicionales además de self.',
    initialCode: `# Define una clase Calculadora
class Calculadora:
    def __init__(self):
        self.resultado = 0
    
    def sumar(self, valor):
        # Completa: suma valor a self.resultado
        pass
    
    def multiplicar(self, valor):
        # Completa: multiplica self.resultado por valor
        pass

# Usa la calculadora
calc = Calculadora()
calc.sumar(10)
calc.multiplicar(3)
print(f"Resultado: {calc.resultado}")`,
    solution: `# Define una clase Calculadora
class Calculadora:
    def __init__(self):
        self.resultado = 0
    
    def sumar(self, valor):
        # Completa: suma valor a self.resultado
        self.resultado += valor
    
    def multiplicar(self, valor):
        # Completa: multiplica self.resultado por valor
        self.resultado *= valor

# Usa la calculadora
calc = Calculadora()
calc.sumar(10)
calc.multiplicar(3)
print(f"Resultado: {calc.resultado}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Operaciones con calculadora',
        input: {},
        expectedOutput: 'Resultado: 30',
        description: 'Verifica que los métodos modifiquen el estado correctamente'
      }
    ],
    hints: [
      'Los métodos pueden recibir parámetros además de self',
      'self.resultado += valor suma al atributo',
      'self.resultado *= valor multiplica el atributo',
      'Los métodos pueden modificar atributos de instancia'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos con parámetros?',
      content: `Los **métodos con parámetros** permiten que los objetos interactúen con datos externos y modifiquen su estado de forma controlada.

### ¿Por qué son útiles?
- **Flexibilidad**: Los métodos pueden trabajar con diferentes valores
- **Reutilización**: Un método puede usarse con múltiples valores
- **Interacción**: Permite que los objetos reciban información externa
- **Modificación controlada**: Cambia el estado del objeto de forma segura

### En el mundo real:
En un sistema de inventario, el método reducir_stock(cantidad) permite reducir el stock en diferentes cantidades según la venta. Sin parámetros, tendrías que crear un método diferente para cada cantidad.`,
      keyPoints: [
        'Los métodos pueden recibir parámetros además de self',
        'Los parámetros permiten flexibilidad',
        'Los métodos pueden modificar atributos con parámetros',
        'self siempre es el primer parámetro'
      ],
      realWorldExample: 'En un sistema de carrito de compras, el método agregar_producto(producto, cantidad) permite agregar diferentes productos en diferentes cantidades.'
    },
    theory: `# Métodos con Parámetros

## ¿Qué son los Métodos con Parámetros?

Los **métodos con parámetros** son métodos que reciben valores adicionales además de \`self\`. Estos valores permiten que el método trabaje con datos externos.

## Sintaxis

\`\`\`python
class Calculadora:
    def sumar(self, valor):
        self.resultado += valor
\`\`\`

## Características

### 1. Parámetros Adicionales
Los métodos pueden recibir múltiples parámetros:
\`\`\`python
def sumar(self, valor):
    self.resultado += valor
\`\`\`

### 2. Modificación de Estado
Los métodos pueden modificar atributos:
\`\`\`python
self.resultado += valor
\`\`\`

### 3. Múltiples Parámetros
Puedes tener varios parámetros:
\`\`\`python
def operacion(self, valor1, valor2):
    self.resultado = valor1 + valor2
\`\`\`

## Ventajas

- **Flexibilidad**: Trabaja con diferentes valores
- **Reutilización**: Un método para múltiples casos
- **Interacción**: Permite recibir datos externos`,
    tips: [
      'self siempre es el primer parámetro',
      'Los parámetros adicionales van después de self',
      'Puedes tener múltiples parámetros',
      'Los métodos pueden modificar atributos con parámetros',
      'Usa nombres descriptivos para los parámetros'
    ],
    advice: [
      'Practica creando métodos con diferentes números de parámetros',
      'Experimenta modificando atributos desde métodos',
      'Aprende a pasar argumentos al llamar métodos',
      'Practica con métodos que reciben múltiples parámetros'
    ]
  },
  {
    id: 4,
    title: 'Métodos que devuelven valores',
    description: 'Aprende a crear métodos que devuelven valores calculados.',
    initialCode: `# Define una clase Rectangulo
class Rectangulo:
    def __init__(self, ancho, alto):
        self.ancho = ancho
        self.alto = alto
    
    def area(self):
        # Completa: devuelve el área (ancho * alto)
        pass
    
    def perimetro(self):
        # Completa: devuelve el perímetro (2 * ancho + 2 * alto)
        pass

# Crea un rectángulo y calcula área y perímetro
rect = Rectangulo(5, 3)
print(f"Área: {rect.area()}")
print(f"Perímetro: {rect.perimetro()}")`,
    solution: `# Define una clase Rectangulo
class Rectangulo:
    def __init__(self, ancho, alto):
        self.ancho = ancho
        self.alto = alto
    
    def area(self):
        # Completa: devuelve el área (ancho * alto)
        return self.ancho * self.alto
    
    def perimetro(self):
        # Completa: devuelve el perímetro (2 * ancho + 2 * alto)
        return 2 * self.ancho + 2 * self.alto

# Crea un rectángulo y calcula área y perímetro
rect = Rectangulo(5, 3)
print(f"Área: {rect.area()}")
print(f"Perímetro: {rect.perimetro()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Calcular área y perímetro',
        input: {},
        expectedOutput: ['Área: 15', 'Perímetro: 16'],
        description: 'Verifica que los métodos devuelvan valores correctos'
      }
    ],
    hints: [
      'Usa return para devolver un valor',
      'Los métodos pueden calcular valores usando atributos',
      'return self.ancho * self.alto calcula el área',
      'Puedes usar los valores devueltos en expresiones'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos que devuelven valores?',
      content: `Los **métodos que devuelven valores** permiten calcular información basada en el estado del objeto sin modificar sus atributos.

### ¿Por qué son importantes?
- **Cálculos**: Permiten calcular valores derivados
- **Inmutabilidad**: No modifican el estado del objeto
- **Reutilización**: Los valores pueden usarse en múltiples lugares
- **Encapsulación**: La lógica de cálculo está dentro de la clase

### En el mundo real:
En un sistema de geometría, el método area() calcula el área basándose en ancho y alto sin modificar estos valores. Sin métodos que devuelven valores, tendrías que calcular el área fuera de la clase.`,
      keyPoints: [
        'return devuelve un valor del método',
        'Los métodos pueden calcular valores usando atributos',
        'Los valores devueltos pueden usarse en expresiones',
        'Los métodos no necesitan modificar atributos'
      ],
      realWorldExample: 'En un sistema de facturación, el método calcular_total() devuelve el total basándose en los productos sin modificar la lista de productos.'
    },
    theory: `# Métodos que Devuelven Valores

## ¿Qué son los Métodos que Devuelven Valores?

Los **métodos que devuelven valores** son métodos que calculan y devuelven un resultado basado en el estado del objeto.

## Sintaxis

\`\`\`python
class Rectangulo:
    def area(self):
        return self.ancho * self.alto
\`\`\`

## Características

### 1. Return Statement
Usa \`return\` para devolver un valor:
\`\`\`python
def area(self):
    return self.ancho * self.alto
\`\`\`

### 2. Uso de Atributos
Los métodos pueden usar atributos para calcular:
\`\`\`python
return self.ancho * self.alto
\`\`\`

### 3. Valores en Expresiones
Los valores devueltos pueden usarse en expresiones:
\`\`\`python
print(f"Área: {rect.area()}")
\`\`\`

## Ventajas

- **Cálculos encapsulados**: La lógica está en la clase
- **Reutilización**: Puedes usar el valor múltiples veces
- **Inmutabilidad**: No modifica el estado del objeto`,
    tips: [
      'Usa return para devolver valores',
      'Los métodos pueden calcular usando atributos',
      'Los valores devueltos pueden usarse en expresiones',
      'No todos los métodos necesitan modificar atributos',
      'Puedes devolver diferentes tipos de datos'
    ],
    advice: [
      'Practica creando métodos que calculen valores',
      'Experimenta usando valores devueltos en expresiones',
      'Aprende a distinguir entre métodos que modifican y que calculan',
      'Practica con métodos que devuelven diferentes tipos'
    ]
  },
  {
    id: 5,
    title: 'Métodos que modifican estado',
    description: 'Aprende a crear métodos que modifican los atributos del objeto.',
    initialCode: `# Define una clase Contador
class Contador:
    def __init__(self):
        self.valor = 0
    
    def incrementar(self):
        # Completa: aumenta self.valor en 1
        pass
    
    def decrementar(self):
        # Completa: disminuye self.valor en 1
        pass
    
    def resetear(self):
        # Completa: pone self.valor en 0
        pass

# Usa el contador
cont = Contador()
cont.incrementar()
cont.incrementar()
cont.decrementar()
print(f"Valor: {cont.valor}")`,
    solution: `# Define una clase Contador
class Contador:
    def __init__(self):
        self.valor = 0
    
    def incrementar(self):
        # Completa: aumenta self.valor en 1
        self.valor += 1
    
    def decrementar(self):
        # Completa: disminuye self.valor en 1
        self.valor -= 1
    
    def resetear(self):
        # Completa: pone self.valor en 0
        self.valor = 0

# Usa el contador
cont = Contador()
cont.incrementar()
cont.incrementar()
cont.decrementar()
print(f"Valor: {cont.valor}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Modificar estado del contador',
        input: {},
        expectedOutput: 'Valor: 1',
        description: 'Verifica que los métodos modifiquen el estado correctamente'
      }
    ],
    hints: [
      'self.valor += 1 incrementa el valor',
      'self.valor -= 1 decrementa el valor',
      'self.valor = 0 asigna un nuevo valor',
      'Los métodos pueden modificar atributos directamente'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos que modifican estado?',
      content: `Los **métodos que modifican estado** permiten cambiar los atributos del objeto de forma controlada y segura.

### ¿Por qué son esenciales?
- **Control**: Modifican el estado de forma controlada
- **Encapsulación**: La lógica de modificación está en la clase
- **Validación**: Pueden validar antes de modificar
- **Consistencia**: Mantienen el objeto en estados válidos

### En el mundo real:
En un sistema de inventario, el método reducir_stock(cantidad) valida que haya suficiente stock antes de reducir. Sin métodos que modifican estado, tendrías que modificar atributos directamente desde fuera de la clase.`,
      keyPoints: [
        'Los métodos pueden modificar atributos con self.atributo = valor',
        'Puedes usar operadores como += y -=',
        'Los métodos pueden validar antes de modificar',
        'La modificación es controlada y segura'
      ],
      realWorldExample: 'En un sistema de cuenta bancaria, el método depositar(monto) valida el monto antes de aumentar el saldo, manteniendo la integridad de los datos.'
    },
    theory: `# Métodos que Modifican Estado

## ¿Qué son los Métodos que Modifican Estado?

Los **métodos que modifican estado** son métodos que cambian los valores de los atributos del objeto.

## Sintaxis

\`\`\`python
class Contador:
    def incrementar(self):
        self.valor += 1
\`\`\`

## Características

### 1. Modificación Directa
Los métodos pueden modificar atributos:
\`\`\`python
self.valor += 1
\`\`\`

### 2. Operadores de Asignación
Puedes usar operadores como += y -=:
\`\`\`python
self.valor += 1  # Incrementa
self.valor -= 1  # Decrementa
\`\`\`

### 3. Asignación Directa
También puedes asignar directamente:
\`\`\`python
self.valor = 0  # Resetea
\`\`\`

## Ventajas

- **Control**: Modificación controlada del estado
- **Validación**: Puedes validar antes de modificar
- **Encapsulación**: La lógica está en la clase`,
    tips: [
      'Usa self.atributo = valor para modificar',
      'Puedes usar operadores +=, -=, *=, /=',
      'Los métodos pueden validar antes de modificar',
      'Modifica atributos desde métodos, no directamente',
      'Asegúrate de mantener el objeto en estados válidos'
    ],
    advice: [
      'Practica creando métodos que modifiquen atributos',
      'Experimenta con diferentes operadores de asignación',
      'Aprende a validar antes de modificar',
      'Practica manteniendo la consistencia del estado'
    ]
  },
  {
    id: 6,
    title: 'Métodos con múltiples parámetros',
    description: 'Aprende a crear métodos que reciben múltiples parámetros.',
    initialCode: `# Define una clase Punto
class Punto:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def mover(self, dx, dy):
        # Completa: mueve el punto sumando dx a x y dy a y
        pass
    
    def distancia_al_origen(self):
        # Completa: calcula la distancia al origen (0,0)
        # Usa: (x² + y²)^0.5
        pass

# Crea un punto y muévelo
p = Punto(3, 4)
print(f"Distancia inicial: {p.distancia_al_origen()}")
p.mover(2, 1)
print(f"Después de mover: ({p.x}, {p.y})")`,
    solution: `# Define una clase Punto
class Punto:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def mover(self, dx, dy):
        # Completa: mueve el punto sumando dx a x y dy a y
        self.x += dx
        self.y += dy
    
    def distancia_al_origen(self):
        # Completa: calcula la distancia al origen (0,0)
        # Usa: (x² + y²)^0.5
        return (self.x ** 2 + self.y ** 2) ** 0.5

# Crea un punto y muévelo
p = Punto(3, 4)
print(f"Distancia inicial: {p.distancia_al_origen()}")
p.mover(2, 1)
print(f"Después de mover: ({p.x}, {p.y})")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Mover punto y calcular distancia',
        input: {},
        expectedOutput: ['Distancia inicial: 5.0', 'Después de mover: (5, 5)'],
        description: 'Verifica que los métodos con múltiples parámetros funcionen'
      }
    ],
    hints: [
      'Los métodos pueden recibir múltiples parámetros',
      'self siempre es el primer parámetro',
      'self.x += dx modifica el atributo x',
      '** es el operador de exponenciación'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos con múltiples parámetros?',
      content: `Los **métodos con múltiples parámetros** permiten que los objetos reciban y procesen múltiples valores de forma flexible y clara.

### ¿Por qué son útiles?
- **Flexibilidad**: Reciben múltiples valores relacionados
- **Claridad**: Los parámetros nombrados hacen el código más legible
- **Funcionalidad**: Permiten operaciones complejas
- **Reutilización**: Un método para múltiples casos

### En el mundo real:
En un sistema de gráficos, el método mover(dx, dy) permite mover un objeto en ambas direcciones con una sola llamada. Sin múltiples parámetros, necesitarías métodos separados para cada dirección.`,
      keyPoints: [
        'Los métodos pueden recibir múltiples parámetros',
        'self siempre es el primer parámetro',
        'Los parámetros se separan por comas',
        'Puedes usar todos los parámetros en el método'
      ],
      realWorldExample: 'En un sistema de coordenadas, el método trasladar(dx, dy, dz) permite mover objetos en 3D con una sola llamada.'
    },
    theory: `# Métodos con Múltiples Parámetros

## ¿Qué son los Métodos con Múltiples Parámetros?

Los **métodos con múltiples parámetros** son métodos que reciben más de un valor además de \`self\`.

## Sintaxis

\`\`\`python
class Punto:
    def mover(self, dx, dy):
        self.x += dx
        self.y += dy
\`\`\`

## Características

### 1. Múltiples Parámetros
Puedes tener varios parámetros:
\`\`\`python
def mover(self, dx, dy):
    self.x += dx
    self.y += dy
\`\`\`

### 2. Uso de Parámetros
Puedes usar todos los parámetros:
\`\`\`python
self.x += dx
self.y += dy
\`\`\`

### 3. Llamada con Argumentos
Pasa argumentos al llamar:
\`\`\`python
p.mover(2, 1)
\`\`\`

## Ventajas

- **Flexibilidad**: Múltiples valores en una llamada
- **Claridad**: Código más legible
- **Funcionalidad**: Permite operaciones complejas`,
    tips: [
      'self siempre es el primer parámetro',
      'Los parámetros se separan por comas',
      'Puedes tener tantos parámetros como necesites',
      'Usa nombres descriptivos para los parámetros',
      'Pasa argumentos en el mismo orden que los parámetros'
    ],
    advice: [
      'Practica creando métodos con diferentes números de parámetros',
      'Experimenta pasando diferentes valores',
      'Aprende a usar todos los parámetros en el método',
      'Practica con métodos que reciben muchos parámetros'
    ]
  },
  {
    id: 7,
    title: 'Atributos con valores por defecto',
    description: 'Aprende a inicializar atributos con valores por defecto en el constructor.',
    initialCode: `# Define una clase Vehiculo
class Vehiculo:
    def __init__(self, marca, modelo, velocidad_maxima=120):
        # Completa: inicializa marca, modelo y velocidad_maxima
        # velocidad_maxima tiene valor por defecto de 120
        pass
    
    def info(self):
        # Completa: devuelve un string con la información del vehículo
        pass

# Crea vehículos con y sin velocidad máxima
auto1 = Vehiculo("Toyota", "Corolla")
auto2 = Vehiculo("Ferrari", "F40", 320)
print(auto1.info())
print(auto2.info())`,
    solution: `# Define una clase Vehiculo
class Vehiculo:
    def __init__(self, marca, modelo, velocidad_maxima=120):
        # Completa: inicializa marca, modelo y velocidad_maxima
        # velocidad_maxima tiene valor por defecto de 120
        self.marca = marca
        self.modelo = modelo
        self.velocidad_maxima = velocidad_maxima
    
    def info(self):
        # Completa: devuelve un string con la información del vehículo
        return f"{self.marca} {self.modelo} - Velocidad máxima: {self.velocidad_maxima} km/h"

# Crea vehículos con y sin velocidad máxima
auto1 = Vehiculo("Toyota", "Corolla")
auto2 = Vehiculo("Ferrari", "F40", 320)
print(auto1.info())
print(auto2.info())`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Valores por defecto',
        input: {},
        expectedOutput: ['Toyota Corolla - Velocidad máxima: 120 km/h', 'Ferrari F40 - Velocidad máxima: 320 km/h'],
        description: 'Verifica que los valores por defecto funcionen correctamente'
      }
    ],
    hints: [
      'Los parámetros con valor por defecto van al final',
      'velocidad_maxima=120 define un valor por defecto',
      'Puedes omitir parámetros con valor por defecto',
      'Los valores por defecto se usan si no se proporciona el argumento'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué valores por defecto?',
      content: `Los **valores por defecto** permiten que algunos parámetros sean opcionales, haciendo las clases más flexibles y fáciles de usar.

### ¿Por qué son útiles?
- **Flexibilidad**: Algunos parámetros pueden ser opcionales
- **Simplicidad**: Valores comunes no necesitan especificarse
- **Conveniencia**: Facilita la creación de objetos
- **Compatibilidad**: Mantiene compatibilidad con código existente

### En el mundo real:
En un sistema de configuración, el método crear_usuario(nombre, email, activo=True) permite crear usuarios activos por defecto. Sin valores por defecto, tendrías que especificar activo=True cada vez.`,
      keyPoints: [
        'Los parámetros con valor por defecto van al final',
        'Puedes omitir argumentos con valor por defecto',
        'Los valores por defecto se usan si no se proporciona el argumento',
        'Facilita la creación de objetos'
      ],
      realWorldExample: 'En un sistema de mensajería, el método enviar_mensaje(destinatario, asunto, prioridad="normal") permite enviar mensajes normales sin especificar prioridad.'
    },
    theory: `# Valores por Defecto en Constructores

## ¿Qué son los Valores por Defecto?

Los **valores por defecto** permiten que algunos parámetros del constructor sean opcionales, usando un valor predefinido si no se proporciona.

## Sintaxis

\`\`\`python
class Vehiculo:
    def __init__(self, marca, modelo, velocidad_maxima=120):
        self.marca = marca
        self.modelo = modelo
        self.velocidad_maxima = velocidad_maxima
\`\`\`

## Características

### 1. Parámetros Opcionales
Los parámetros con valor por defecto son opcionales:
\`\`\`python
def __init__(self, marca, modelo, velocidad_maxima=120):
\`\`\`

### 2. Uso con Valor por Defecto
Puedes omitir el argumento:
\`\`\`python
auto1 = Vehiculo("Toyota", "Corolla")  # velocidad_maxima = 120
\`\`\`

### 3. Sobrescribir Valor por Defecto
Puedes proporcionar un valor diferente:
\`\`\`python
auto2 = Vehiculo("Ferrari", "F40", 320)  # velocidad_maxima = 320
\`\`\`

## Ventajas

- **Flexibilidad**: Parámetros opcionales
- **Simplicidad**: Valores comunes no necesitan especificarse
- **Conveniencia**: Facilita la creación de objetos`,
    tips: [
      'Los parámetros con valor por defecto van al final',
      'Puedes tener múltiples parámetros con valor por defecto',
      'Puedes omitir argumentos con valor por defecto',
      'Los valores por defecto se usan si no se proporciona el argumento',
      'Puedes sobrescribir valores por defecto proporcionando el argumento'
    ],
    advice: [
      'Practica creando constructores con valores por defecto',
      'Experimenta omitiendo y proporcionando argumentos',
      'Aprende a usar múltiples parámetros con valor por defecto',
      'Practica con valores por defecto de diferentes tipos'
    ]
  },
  {
    id: 8,
    title: 'Métodos que validan datos',
    description: 'Aprende a crear métodos que validan datos antes de modificar el estado.',
    initialCode: `# Define una clase CuentaBancaria
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial=0):
        self.titular = titular
        self.saldo = saldo_inicial
    
    def retirar(self, monto):
        # Completa: retira monto solo si hay suficiente saldo
        # Si hay suficiente saldo, resta monto a self.saldo y devuelve True
        # Si no hay suficiente saldo, devuelve False
        pass
    
    def depositar(self, monto):
        # Completa: deposita monto solo si es positivo
        # Si monto > 0, suma monto a self.saldo y devuelve True
        # Si monto <= 0, devuelve False
        pass

# Prueba la cuenta
cuenta = CuentaBancaria("Juan", 100)
print(f"Saldo inicial: {cuenta.saldo}")
print(f"Retiro exitoso: {cuenta.retirar(50)}")
print(f"Retiro fallido: {cuenta.retirar(100)}")
print(f"Saldo final: {cuenta.saldo}")`,
    solution: `# Define una clase CuentaBancaria
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial=0):
        self.titular = titular
        self.saldo = saldo_inicial
    
    def retirar(self, monto):
        # Completa: retira monto solo si hay suficiente saldo
        # Si hay suficiente saldo, resta monto a self.saldo y devuelve True
        # Si no hay suficiente saldo, devuelve False
        if self.saldo >= monto:
            self.saldo -= monto
            return True
        return False
    
    def depositar(self, monto):
        # Completa: deposita monto solo si es positivo
        # Si monto > 0, suma monto a self.saldo y devuelve True
        # Si monto <= 0, devuelve False
        if monto > 0:
            self.saldo += monto
            return True
        return False

# Prueba la cuenta
cuenta = CuentaBancaria("Juan", 100)
print(f"Saldo inicial: {cuenta.saldo}")
print(f"Retiro exitoso: {cuenta.retirar(50)}")
print(f"Retiro fallido: {cuenta.retirar(100)}")
print(f"Saldo final: {cuenta.saldo}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Validación de operaciones',
        input: {},
        expectedOutput: ['Saldo inicial: 100', 'Retiro exitoso: True', 'Retiro fallido: False', 'Saldo final: 50'],
        description: 'Verifica que las validaciones funcionen correctamente'
      }
    ],
    hints: [
      'Usa if para validar antes de modificar',
      'self.saldo >= monto verifica si hay suficiente saldo',
      'return True indica éxito, return False indica fallo',
      'Los métodos pueden devolver valores booleanos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué validar datos en métodos?',
      content: `Los **métodos que validan datos** aseguran que el objeto siempre esté en un estado válido, previniendo errores y manteniendo la integridad de los datos.

### ¿Por qué son críticos?
- **Integridad**: Mantienen el objeto en estados válidos
- **Prevención de errores**: Evitan operaciones inválidas
- **Seguridad**: Protegen contra datos incorrectos
- **Robustez**: Hacen el código más confiable

### En el mundo real:
En un sistema bancario, el método retirar(monto) valida que haya suficiente saldo antes de permitir el retiro. Sin validación, podrías retirar más dinero del disponible.`,
      keyPoints: [
        'Valida antes de modificar el estado',
        'Usa if para verificar condiciones',
        'Devuelve True/False para indicar éxito/fallo',
        'Mantén el objeto en estados válidos'
      ],
      realWorldExample: 'En un sistema de inventario, el método reducir_stock(cantidad) valida que haya suficiente stock antes de reducir, evitando stock negativo.'
    },
    theory: `# Métodos que Validan Datos

## ¿Qué son los Métodos que Validan Datos?

Los **métodos que validan datos** son métodos que verifican condiciones antes de modificar el estado del objeto, asegurando que las operaciones sean válidas.

## Sintaxis

\`\`\`python
class CuentaBancaria:
    def retirar(self, monto):
        if self.saldo >= monto:
            self.saldo -= monto
            return True
        return False
\`\`\`

## Características

### 1. Validación con if
Verifica condiciones antes de modificar:
\`\`\`python
if self.saldo >= monto:
    self.saldo -= monto
    return True
\`\`\`

### 2. Valores de Retorno
Devuelve True/False para indicar éxito/fallo:
\`\`\`python
return True   # Éxito
return False  # Fallo
\`\`\`

### 3. Múltiples Validaciones
Puedes validar múltiples condiciones:
\`\`\`python
if monto > 0 and self.saldo >= monto:
    self.saldo -= monto
    return True
\`\`\`

## Ventajas

- **Integridad**: Mantiene el objeto en estados válidos
- **Prevención**: Evita operaciones inválidas
- **Robustez**: Código más confiable`,
    tips: [
      'Valida antes de modificar el estado',
      'Usa if para verificar condiciones',
      'Devuelve True/False para indicar éxito/fallo',
      'Puedes validar múltiples condiciones',
      'Mantén el objeto en estados válidos siempre'
    ],
    advice: [
      'Practica creando métodos con validaciones',
      'Experimenta con diferentes condiciones de validación',
      'Aprende a devolver valores booleanos',
      'Practica validando múltiples condiciones'
    ]
  },
  {
    id: 9,
    title: 'Métodos estáticos y de clase',
    description: 'Aprende sobre métodos que no requieren una instancia (métodos estáticos).',
    initialCode: `# Define una clase Utilidades
class Utilidades:
    @staticmethod
    def sumar(a, b):
        # Completa: devuelve la suma de a y b
        pass
    
    @staticmethod
    def es_par(numero):
        # Completa: devuelve True si numero es par, False si es impar
        pass

# Usa los métodos estáticos sin crear instancia
print(f"Suma: {Utilidades.sumar(5, 3)}")
print(f"¿8 es par?: {Utilidades.es_par(8)}")`,
    solution: `# Define una clase Utilidades
class Utilidades:
    @staticmethod
    def sumar(a, b):
        # Completa: devuelve la suma de a y b
        return a + b
    
    @staticmethod
    def es_par(numero):
        # Completa: devuelve True si numero es par, False si es impar
        return numero % 2 == 0

# Usa los métodos estáticos sin crear instancia
print(f"Suma: {Utilidades.sumar(5, 3)}")
print(f"¿8 es par?: {Utilidades.es_par(8)}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Métodos estáticos',
        input: {},
        expectedOutput: ['Suma: 8', '¿8 es par?: True'],
        description: 'Verifica que los métodos estáticos funcionen sin instancia'
      }
    ],
    hints: [
      '@staticmethod marca un método como estático',
      'Los métodos estáticos no requieren self',
      'Puedes llamarlos desde la clase sin crear instancia',
      'No tienen acceso a atributos de instancia'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos estáticos?',
      content: `Los **métodos estáticos** son métodos que no requieren una instancia de la clase. Son útiles para funciones relacionadas con la clase pero que no necesitan acceso al estado del objeto.

### ¿Por qué son útiles?
- **Utilidad**: Funciones relacionadas con la clase pero independientes
- **Organización**: Agrupan funciones relacionadas
- **Eficiencia**: No requieren crear instancias
- **Claridad**: Indican que no dependen del estado del objeto

### En el mundo real:
En un sistema de matemáticas, la clase Calculadora tiene métodos estáticos como sumar() y multiplicar() que no necesitan estado. Sin métodos estáticos, tendrías que crear una instancia solo para usar funciones simples.`,
      keyPoints: [
        '@staticmethod marca un método como estático',
        'Los métodos estáticos no requieren self',
        'Puedes llamarlos desde la clase directamente',
        'No tienen acceso a atributos de instancia'
      ],
      realWorldExample: 'En un sistema de validación, la clase Validador tiene métodos estáticos como es_email_valido() que no necesitan estado del objeto.'
    },
    theory: `# Métodos Estáticos

## ¿Qué son los Métodos Estáticos?

Los **métodos estáticos** son métodos que pertenecen a la clase pero no requieren una instancia. No tienen acceso a \`self\` ni a atributos de instancia.

## Sintaxis

\`\`\`python
class Utilidades:
    @staticmethod
    def sumar(a, b):
        return a + b
\`\`\`

## Características

### 1. Decorador @staticmethod
Marca el método como estático:
\`\`\`python
@staticmethod
def sumar(a, b):
\`\`\`

### 2. Sin self
Los métodos estáticos no tienen \`self\`:
\`\`\`python
def sumar(a, b):  # No tiene self
    return a + b
\`\`\`

### 3. Llamada desde la Clase
Puedes llamarlos desde la clase:
\`\`\`python
Utilidades.sumar(5, 3)  # Sin crear instancia
\`\`\`

## Ventajas

- **Utilidad**: Funciones relacionadas pero independientes
- **Eficiencia**: No requieren instancias
- **Organización**: Agrupan funciones relacionadas`,
    tips: [
      '@staticmethod marca un método como estático',
      'Los métodos estáticos no tienen self',
      'Puedes llamarlos desde la clase directamente',
      'No tienen acceso a atributos de instancia',
      'Úsalos para funciones que no necesitan estado'
    ],
    advice: [
      'Practica creando métodos estáticos',
      'Experimenta llamándolos desde la clase',
      'Aprende cuándo usar métodos estáticos vs métodos de instancia',
      'Practica con métodos estáticos que realizan cálculos'
    ]
  },
  {
    id: 10,
    title: 'Métodos que trabajan con otros objetos',
    description: 'Aprende a crear métodos que interactúan con otros objetos de la misma o diferente clase.',
    initialCode: `# Define clases Persona y Mensaje
class Persona:
    def __init__(self, nombre):
        self.nombre = nombre
        self.mensajes = []
    
    def recibir_mensaje(self, mensaje):
        # Completa: agrega mensaje a self.mensajes
        pass
    
    def enviar_mensaje(self, destinatario, texto):
        # Completa: crea un Mensaje y lo agrega a destinatario.mensajes
        # Usa: mensaje = Mensaje(self.nombre, texto)
        pass

class Mensaje:
    def __init__(self, remitente, texto):
        self.remitente = remitente
        self.texto = texto

# Prueba el sistema de mensajería
alice = Persona("Alice")
bob = Persona("Bob")
alice.enviar_mensaje(bob, "Hola Bob!")
print(f"Mensajes de Bob: {len(bob.mensajes)}")`,
    solution: `# Define clases Persona y Mensaje
class Persona:
    def __init__(self, nombre):
        self.nombre = nombre
        self.mensajes = []
    
    def recibir_mensaje(self, mensaje):
        # Completa: agrega mensaje a self.mensajes
        self.mensajes.append(mensaje)
    
    def enviar_mensaje(self, destinatario, texto):
        # Completa: crea un Mensaje y lo agrega a destinatario.mensajes
        # Usa: mensaje = Mensaje(self.nombre, texto)
        mensaje = Mensaje(self.nombre, texto)
        destinatario.recibir_mensaje(mensaje)

class Mensaje:
    def __init__(self, remitente, texto):
        self.remitente = remitente
        self.texto = texto

# Prueba el sistema de mensajería
alice = Persona("Alice")
bob = Persona("Bob")
alice.enviar_mensaje(bob, "Hola Bob!")
print(f"Mensajes de Bob: {len(bob.mensajes)}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Interacción entre objetos',
        input: {},
        expectedOutput: 'Mensajes de Bob: 1',
        description: 'Verifica que los objetos puedan interactuar entre sí'
      }
    ],
    hints: [
      'Los métodos pueden recibir otros objetos como parámetros',
      'destinatario.recibir_mensaje() llama a un método de otro objeto',
      'Puedes crear objetos dentro de métodos',
      'Los objetos pueden trabajar juntos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué métodos que trabajan con otros objetos?',
      content: `Los **métodos que trabajan con otros objetos** permiten que los objetos interactúen entre sí, creando sistemas más complejos y realistas.

### ¿Por qué son importantes?
- **Interacción**: Permiten que objetos trabajen juntos
- **Modelado**: Reflejan relaciones del mundo real
- **Complejidad**: Permiten sistemas más complejos
- **Reutilización**: Objetos pueden trabajar con diferentes objetos

### En el mundo real:
En un sistema de mensajería, el método enviar_mensaje(destinatario, texto) crea un mensaje y lo entrega a otro objeto Persona. Sin esta interacción, los objetos estarían aislados.`,
      keyPoints: [
        'Los métodos pueden recibir otros objetos como parámetros',
        'Puedes llamar métodos de otros objetos',
        'Los objetos pueden crear otros objetos',
        'La interacción permite sistemas complejos'
      ],
      realWorldExample: 'En un sistema de e-commerce, el método agregar_al_carrito(producto) de la clase Carrito interactúa con objetos Producto para agregarlos.'
    },
    theory: `# Métodos que Trabajan con Otros Objetos

## ¿Qué son los Métodos que Trabajan con Otros Objetos?

Los **métodos que trabajan con otros objetos** son métodos que reciben otros objetos como parámetros y pueden llamar sus métodos o acceder a sus atributos.

## Sintaxis

\`\`\`python
class Persona:
    def enviar_mensaje(self, destinatario, texto):
        mensaje = Mensaje(self.nombre, texto)
        destinatario.recibir_mensaje(mensaje)
\`\`\`

## Características

### 1. Objetos como Parámetros
Los métodos pueden recibir otros objetos:
\`\`\`python
def enviar_mensaje(self, destinatario, texto):
\`\`\`

### 2. Llamar Métodos de Otros Objetos
Puedes llamar métodos de otros objetos:
\`\`\`python
destinatario.recibir_mensaje(mensaje)
\`\`\`

### 3. Crear Objetos
Los métodos pueden crear otros objetos:
\`\`\`python
mensaje = Mensaje(self.nombre, texto)
\`\`\`

## Ventajas

- **Interacción**: Permite que objetos trabajen juntos
- **Modelado**: Refleja relaciones del mundo real
- **Complejidad**: Permite sistemas más complejos`,
    tips: [
      'Los métodos pueden recibir otros objetos como parámetros',
      'Puedes llamar métodos de otros objetos',
      'Los objetos pueden crear otros objetos',
      'La interacción permite sistemas complejos',
      'Asegúrate de entender las relaciones entre objetos'
    ],
    advice: [
      'Practica creando métodos que reciben otros objetos',
      'Experimenta llamando métodos de otros objetos',
      'Aprende a modelar relaciones entre objetos',
      'Practica con sistemas que involucren múltiples objetos'
    ]
  },
  // ========== HERENCIA (11-16) ==========
  {
    id: 11,
    title: 'Herencia básica',
    description: 'Aprende a crear una clase que hereda de otra clase.',
    initialCode: `# Define clase base Animal
class Animal:
    def __init__(self, nombre):
        self.nombre = nombre
    
    def hacer_sonido(self):
        return "Hace un sonido"

# Define clase Perro que hereda de Animal
class Perro(Animal):
    def __init__(self, nombre, raza):
        # Completa: llama al constructor de Animal con super()
        pass
        self.raza = raza
    
    def hacer_sonido(self):
        # Completa: devuelve "Guau!"
        pass

# Crea un perro y prueba la herencia
perro = Perro("Max", "Labrador")
print(f"{perro.nombre} dice: {perro.hacer_sonido()}")`,
    solution: `# Define clase base Animal
class Animal:
    def __init__(self, nombre):
        self.nombre = nombre
    
    def hacer_sonido(self):
        return "Hace un sonido"

# Define clase Perro que hereda de Animal
class Perro(Animal):
    def __init__(self, nombre, raza):
        # Completa: llama al constructor de Animal con super()
        super().__init__(nombre)
        self.raza = raza
    
    def hacer_sonido(self):
        # Completa: devuelve "Guau!"
        return "Guau!"

# Crea un perro y prueba la herencia
perro = Perro("Max", "Labrador")
print(f"{perro.nombre} dice: {perro.hacer_sonido()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Herencia básica',
        input: {},
        expectedOutput: 'Max dice: Guau!',
        description: 'Verifica que la herencia funcione correctamente'
      }
    ],
    hints: [
      'class Perro(Animal) hereda de Animal',
      'super().__init__(nombre) llama al constructor de la clase padre',
      'Puedes sobrescribir métodos de la clase padre',
      'Los métodos sobrescritos reemplazan los de la clase padre'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar herencia?',
      content: `La **herencia** permite crear nuevas clases basadas en clases existentes, reutilizando código y extendiendo funcionalidad.

### ¿Por qué es esencial?
- **Reutilización**: Reutiliza código de clases existentes
- **Extensión**: Agrega nueva funcionalidad sin modificar la clase base
- **Organización**: Crea jerarquías lógicas de clases
- **Mantenibilidad**: Cambios en la clase base afectan a las clases hijas

### En el mundo real:
En un sistema de vehículos, la clase Vehiculo define comportamiento común, y Auto y Moto heredan y agregan funcionalidad específica. Sin herencia, tendrías que duplicar código en cada clase.`,
      keyPoints: [
        'class Hijo(Padre) hereda de Padre',
        'super() permite acceder a la clase padre',
        'Puedes sobrescribir métodos de la clase padre',
        'La herencia permite reutilizar código'
      ],
      realWorldExample: 'En un sistema de empleados, la clase Empleado define comportamiento común, y Gerente y Desarrollador heredan y agregan funcionalidad específica.'
    },
    theory: `# Herencia en Python

## ¿Qué es la Herencia?

La **herencia** permite crear una nueva clase basada en una clase existente, heredando sus atributos y métodos.

## Sintaxis

\`\`\`python
class Animal:
    def __init__(self, nombre):
        self.nombre = nombre

class Perro(Animal):
    def __init__(self, nombre, raza):
        super().__init__(nombre)
        self.raza = raza
\`\`\`

## Características

### 1. Definición de Herencia
\`class Hijo(Padre)\` hereda de Padre:
\`\`\`python
class Perro(Animal):
\`\`\`

### 2. Llamar Constructor Padre
Usa \`super()\` para llamar al constructor padre:
\`\`\`python
super().__init__(nombre)
\`\`\`

### 3. Sobrescribir Métodos
Puedes sobrescribir métodos de la clase padre:
\`\`\`python
def hacer_sonido(self):
    return "Guau!"  # Sobrescribe el método de Animal
\`\`\`

## Ventajas

- **Reutilización**: Reutiliza código existente
- **Extensión**: Agrega funcionalidad sin modificar la clase base
- **Organización**: Crea jerarquías lógicas`,
    tips: [
      'class Hijo(Padre) hereda de Padre',
      'super() permite acceder a la clase padre',
      'super().__init__() llama al constructor padre',
      'Puedes sobrescribir métodos de la clase padre',
      'La clase hija hereda todos los métodos y atributos'
    ],
    advice: [
      'Practica creando clases que heredan',
      'Experimenta llamando métodos de la clase padre',
      'Aprende a sobrescribir métodos correctamente',
      'Practica con jerarquías de clases simples'
    ]
  },
  {
    id: 12,
    title: 'Agregar métodos en clases hijas',
    description: 'Aprende a agregar nuevos métodos en clases que heredan.',
    initialCode: `# Define clase base Vehiculo
class Vehiculo:
    def __init__(self, marca, modelo):
        self.marca = marca
        self.modelo = modelo
    
    def info(self):
        return f"{self.marca} {self.modelo}"

# Define clase Auto que hereda de Vehiculo
class Auto(Vehiculo):
    def __init__(self, marca, modelo, puertas):
        super().__init__(marca, modelo)
        self.puertas = puertas
    
    def abrir_puertas(self):
        # Completa: devuelve "Abriendo {puertas} puertas"
        pass

# Crea un auto y prueba los métodos
auto = Auto("Toyota", "Corolla", 4)
print(auto.info())
print(auto.abrir_puertas())`,
    solution: `# Define clase base Vehiculo
class Vehiculo:
    def __init__(self, marca, modelo):
        self.marca = marca
        self.modelo = modelo
    
    def info(self):
        return f"{self.marca} {self.modelo}"

# Define clase Auto que hereda de Vehiculo
class Auto(Vehiculo):
    def __init__(self, marca, modelo, puertas):
        super().__init__(marca, modelo)
        self.puertas = puertas
    
    def abrir_puertas(self):
        # Completa: devuelve "Abriendo {puertas} puertas"
        return f"Abriendo {self.puertas} puertas"

# Crea un auto y prueba los métodos
auto = Auto("Toyota", "Corolla", 4)
print(auto.info())
print(auto.abrir_puertas())`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Métodos nuevos en clase hija',
        input: {},
        expectedOutput: ['Toyota Corolla', 'Abriendo 4 puertas'],
        description: 'Verifica que puedas agregar métodos nuevos en clases hijas'
      }
    ],
    hints: [
      'Las clases hijas pueden agregar nuevos métodos',
      'Los métodos nuevos son específicos de la clase hija',
      'Puedes usar métodos heredados junto con métodos nuevos',
      'Los métodos nuevos no afectan a la clase padre'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué agregar métodos en clases hijas?',
      content: `Agregar **métodos nuevos en clases hijas** permite extender la funcionalidad sin modificar la clase base, manteniendo la clase padre simple y las clases hijas especializadas.

### ¿Por qué es útil?
- **Especialización**: Cada clase hija tiene su propia funcionalidad
- **Extensión**: Agrega funcionalidad sin modificar la clase base
- **Organización**: Mantiene la clase base simple
- **Flexibilidad**: Cada clase hija puede tener métodos únicos

### En el mundo real:
En un sistema de vehículos, Vehiculo tiene métodos comunes, y Auto agrega abrir_puertas() mientras Moto agrega poner_casco(). Sin métodos nuevos en clases hijas, todas las clases tendrían los mismos métodos.`,
      keyPoints: [
        'Las clases hijas pueden agregar métodos nuevos',
        'Los métodos nuevos son específicos de la clase hija',
        'No afectan a la clase padre ni a otras clases hijas',
        'Permiten especializar cada clase hija'
      ],
      realWorldExample: 'En un sistema de empleados, Empleado tiene métodos comunes, y Gerente agrega aprobar_vacaciones() mientras Desarrollador agrega escribir_codigo().'
    },
    theory: `# Agregar Métodos en Clases Hijas

## ¿Qué son los Métodos Nuevos en Clases Hijas?

Los **métodos nuevos en clases hijas** son métodos que solo existen en la clase hija, no en la clase padre.

## Sintaxis

\`\`\`python
class Auto(Vehiculo):
    def abrir_puertas(self):
        return f"Abriendo {self.puertas} puertas"
\`\`\`

## Características

### 1. Métodos Específicos
Los métodos nuevos son específicos de la clase hija:
\`\`\`python
def abrir_puertas(self):
    return f"Abriendo {self.puertas} puertas"
\`\`\`

### 2. Uso de Atributos
Pueden usar atributos propios y heredados:
\`\`\`python
return f"Abriendo {self.puertas} puertas"  # self.puertas es propio
\`\`\`

### 3. Independencia
No afectan a la clase padre ni a otras clases hijas:
\`\`\`python
# Solo Auto tiene abrir_puertas()
\`\`\`

## Ventajas

- **Especialización**: Cada clase hija tiene su propia funcionalidad
- **Extensión**: Agrega funcionalidad sin modificar la clase base
- **Organización**: Mantiene la clase base simple`,
    tips: [
      'Las clases hijas pueden agregar métodos nuevos',
      'Los métodos nuevos son específicos de la clase hija',
      'Puedes usar atributos propios y heredados',
      'Los métodos nuevos no afectan a la clase padre',
      'Cada clase hija puede tener métodos únicos'
    ],
    advice: [
      'Practica agregando métodos nuevos en clases hijas',
      'Experimenta usando atributos propios y heredados',
      'Aprende a especializar cada clase hija',
      'Practica con jerarquías que tienen múltiples clases hijas'
    ]
  },
  {
    id: 13,
    title: 'Sobrescribir métodos con super()',
    description: 'Aprende a extender métodos de la clase padre usando super() en lugar de reemplazarlos completamente.',
    initialCode: `# Define clase base Empleado
class Empleado:
    def __init__(self, nombre, salario):
        self.nombre = nombre
        self.salario = salario
    
    def calcular_pago(self):
        return self.salario

# Define clase Gerente que hereda de Empleado
class Gerente(Empleado):
    def __init__(self, nombre, salario, bono):
        super().__init__(nombre, salario)
        self.bono = bono
    
    def calcular_pago(self):
        # Completa: llama a calcular_pago() de Empleado y suma el bono
        # Usa: pago_base = super().calcular_pago()
        pass

# Crea un gerente y calcula el pago
gerente = Gerente("Ana", 5000, 1000)
print(f"Pago total: {gerente.calcular_pago()}")`,
    solution: `# Define clase base Empleado
class Empleado:
    def __init__(self, nombre, salario):
        self.nombre = nombre
        self.salario = salario
    
    def calcular_pago(self):
        return self.salario

# Define clase Gerente que hereda de Empleado
class Gerente(Empleado):
    def __init__(self, nombre, salario, bono):
        super().__init__(nombre, salario)
        self.bono = bono
    
    def calcular_pago(self):
        # Completa: llama a calcular_pago() de Empleado y suma el bono
        # Usa: pago_base = super().calcular_pago()
        pago_base = super().calcular_pago()
        return pago_base + self.bono

# Crea un gerente y calcula el pago
gerente = Gerente("Ana", 5000, 1000)
print(f"Pago total: {gerente.calcular_pago()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Extender método con super()',
        input: {},
        expectedOutput: 'Pago total: 6000',
        description: 'Verifica que puedas extender métodos usando super()'
      }
    ],
    hints: [
      'super().metodo() llama al método de la clase padre',
      'Puedes usar el resultado del método padre',
      'Extiende funcionalidad en lugar de reemplazarla',
      'Útil cuando quieres agregar comportamiento adicional'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar super() para extender métodos?',
      content: `Usar **super() para extender métodos** permite agregar funcionalidad adicional sin perder el comportamiento de la clase padre, manteniendo el código DRY (Don't Repeat Yourself).

### ¿Por qué es mejor que reemplazar?
- **Reutilización**: Reutiliza el código de la clase padre
- **Mantenibilidad**: Cambios en la clase padre se reflejan automáticamente
- **Extensión**: Agrega funcionalidad sin duplicar código
- **Consistencia**: Mantiene el comportamiento base

### En el mundo real:
En un sistema de facturación, Empleado.calcular_pago() calcula el salario base, y Gerente.calcular_pago() lo extiende agregando el bono. Sin super(), tendrías que duplicar el cálculo del salario base.`,
      keyPoints: [
        'super().metodo() llama al método de la clase padre',
        'Puedes usar el resultado del método padre',
        'Extiende funcionalidad en lugar de reemplazarla',
        'Mantiene el código DRY'
      ],
      realWorldExample: 'En un sistema de formas, Forma.area() calcula el área base, y Circulo.area() lo extiende con cálculos específicos del círculo.'
    },
    theory: `# Extender Métodos con super()

## ¿Qué es Extender Métodos con super()?

**Extender métodos con super()** significa llamar al método de la clase padre y agregar funcionalidad adicional, en lugar de reemplazarlo completamente.

## Sintaxis

\`\`\`python
class Gerente(Empleado):
    def calcular_pago(self):
        pago_base = super().calcular_pago()
        return pago_base + self.bono
\`\`\`

## Características

### 1. Llamar Método Padre
Usa \`super().metodo()\` para llamar al método padre:
\`\`\`python
pago_base = super().calcular_pago()
\`\`\`

### 2. Extender Funcionalidad
Agrega funcionalidad adicional:
\`\`\`python
return pago_base + self.bono
\`\`\`

### 3. Reutilización
Reutiliza el código de la clase padre:
\`\`\`python
# No necesitas duplicar el cálculo del salario base
\`\`\`

## Ventajas

- **Reutilización**: Reutiliza código de la clase padre
- **Mantenibilidad**: Cambios se reflejan automáticamente
- **Extensión**: Agrega funcionalidad sin duplicar código`,
    tips: [
      'super().metodo() llama al método de la clase padre',
      'Puedes usar el resultado del método padre',
      'Extiende funcionalidad en lugar de reemplazarla',
      'Útil cuando quieres agregar comportamiento adicional',
      'Mantiene el código DRY'
    ],
    advice: [
      'Practica extendiendo métodos con super()',
      'Experimenta usando resultados de métodos padre',
      'Aprende cuándo extender vs reemplazar',
      'Practica con métodos que calculan valores'
    ]
  },
  {
    id: 14,
    title: 'Herencia múltiple básica',
    description: 'Aprende sobre herencia múltiple: una clase puede heredar de múltiples clases padre.',
    initialCode: `# Define clases base
class Volador:
    def volar(self):
        return "Volando"

class Nadador:
    def nadar(self):
        return "Nadando"

# Define clase Pato que hereda de ambas
class Pato(Volador, Nadador):
    def __init__(self, nombre):
        self.nombre = nombre
    
    def presentarse(self):
        # Completa: devuelve "{nombre} puede {volar} y {nadar}"
        # Usa: self.volar() y self.nadar()
        pass

# Crea un pato y prueba la herencia múltiple
pato = Pato("Donald")
print(pato.presentarse())`,
    solution: `# Define clases base
class Volador:
    def volar(self):
        return "Volando"

class Nadador:
    def nadar(self):
        return "Nadando"

# Define clase Pato que hereda de ambas
class Pato(Volador, Nadador):
    def __init__(self, nombre):
        self.nombre = nombre
    
    def presentarse(self):
        # Completa: devuelve "{nombre} puede {volar} y {nadar}"
        # Usa: self.volar() y self.nadar()
        return f"{self.nombre} puede {self.volar()} y {self.nadar()}"

# Crea un pato y prueba la herencia múltiple
pato = Pato("Donald")
print(pato.presentarse())`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Herencia múltiple',
        input: {},
        expectedOutput: 'Donald puede Volando y Nadando',
        description: 'Verifica que la herencia múltiple funcione correctamente'
      }
    ],
    hints: [
      'class Pato(Volador, Nadador) hereda de ambas clases',
      'Puedes usar métodos de ambas clases padre',
      'La herencia múltiple permite combinar funcionalidad',
      'Python resuelve conflictos con el MRO (Method Resolution Order)'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué herencia múltiple?',
      content: `La **herencia múltiple** permite que una clase herede de múltiples clases padre, combinando funcionalidad de diferentes fuentes.

### ¿Por qué es útil?
- **Combinación**: Combina funcionalidad de múltiples clases
- **Flexibilidad**: Permite crear clases con características diversas
- **Reutilización**: Reutiliza código de múltiples clases
- **Modelado**: Refleja relaciones complejas del mundo real

### En el mundo real:
En un sistema de vehículos, una clase Anfibio puede heredar de Terrestre y Acuatico, combinando funcionalidad de ambas. Sin herencia múltiple, tendrías que duplicar código o crear jerarquías complejas.`,
      keyPoints: [
        'class Hijo(Padre1, Padre2) hereda de ambas',
        'Puedes usar métodos de todas las clases padre',
        'Python resuelve conflictos con MRO',
        'Útil para combinar funcionalidad'
      ],
      realWorldExample: 'En un sistema de empleados, una clase GerenteDesarrollador puede heredar de Gerente y Desarrollador, combinando funcionalidad de ambas.'
    },
    theory: `# Herencia Múltiple

## ¿Qué es la Herencia Múltiple?

La **herencia múltiple** permite que una clase herede de múltiples clases padre, combinando su funcionalidad.

## Sintaxis

\`\`\`python
class Pato(Volador, Nadador):
    def __init__(self, nombre):
        self.nombre = nombre
\`\`\`

## Características

### 1. Múltiples Clases Padre
Puedes heredar de varias clases:
\`\`\`python
class Pato(Volador, Nadador):
\`\`\`

### 2. Acceso a Métodos
Puedes usar métodos de todas las clases padre:
\`\`\`python
self.volar()  # De Volador
self.nadar()  # De Nadador
\`\`\`

### 3. MRO (Method Resolution Order)
Python resuelve conflictos con el orden de herencia:
\`\`\`python
# El orden importa: (Volador, Nadador)
\`\`\`

## Ventajas

- **Combinación**: Combina funcionalidad de múltiples clases
- **Flexibilidad**: Permite crear clases diversas
- **Reutilización**: Reutiliza código de múltiples fuentes`,
    tips: [
      'class Hijo(Padre1, Padre2) hereda de ambas',
      'Puedes usar métodos de todas las clases padre',
      'El orden de herencia importa (MRO)',
      'Útil para combinar funcionalidad',
      'Ten cuidado con conflictos de nombres'
    ],
    advice: [
      'Practica con herencia múltiple simple',
      'Experimenta usando métodos de múltiples clases padre',
      'Aprende sobre MRO (Method Resolution Order)',
      'Practica con clases que combinan funcionalidad'
    ]
  },
  {
    id: 15,
    title: 'Verificar tipo con isinstance',
    description: 'Aprende a verificar si un objeto es instancia de una clase usando isinstance().',
    initialCode: `# Define clases
class Animal:
    def __init__(self, nombre):
        self.nombre = nombre

class Perro(Animal):
    def __init__(self, nombre, raza):
        super().__init__(nombre)
        self.raza = raza

class Gato(Animal):
    def __init__(self, nombre):
        super().__init__(nombre)

# Función que verifica tipos
def describir_animal(animal):
    # Completa: si es Perro, devuelve "Perro llamado {nombre}"
    # Si es Gato, devuelve "Gato llamado {nombre}"
    # Si es Animal, devuelve "Animal llamado {nombre}"
    # Usa: isinstance(objeto, Clase)
    pass

# Prueba la función
perro = Perro("Max", "Labrador")
gato = Gato("Whiskers")
print(describir_animal(perro))
print(describir_animal(gato))`,
    solution: `# Define clases
class Animal:
    def __init__(self, nombre):
        self.nombre = nombre

class Perro(Animal):
    def __init__(self, nombre, raza):
        super().__init__(nombre)
        self.raza = raza

class Gato(Animal):
    def __init__(self, nombre):
        super().__init__(nombre)

# Función que verifica tipos
def describir_animal(animal):
    # Completa: si es Perro, devuelve "Perro llamado {nombre}"
    # Si es Gato, devuelve "Gato llamado {nombre}"
    # Si es Animal, devuelve "Animal llamado {nombre}"
    # Usa: isinstance(objeto, Clase)
    if isinstance(animal, Perro):
        return f"Perro llamado {animal.nombre}"
    elif isinstance(animal, Gato):
        return f"Gato llamado {animal.nombre}"
    elif isinstance(animal, Animal):
        return f"Animal llamado {animal.nombre}"

# Prueba la función
perro = Perro("Max", "Labrador")
gato = Gato("Whiskers")
print(describir_animal(perro))
print(describir_animal(gato))`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Verificar tipos con isinstance',
        input: {},
        expectedOutput: ['Perro llamado Max', 'Gato llamado Whiskers'],
        description: 'Verifica que isinstance() funcione correctamente'
      }
    ],
    hints: [
      'isinstance(objeto, Clase) verifica si objeto es instancia de Clase',
      'isinstance también verifica clases padre',
      'Útil para verificar tipos antes de usar métodos específicos',
      'Más recomendado que type() == Clase'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar isinstance()?',
      content: `**isinstance()** permite verificar si un objeto es instancia de una clase específica, incluyendo clases padre, lo cual es esencial para polimorfismo y verificación de tipos.

### ¿Por qué es importante?
- **Polimorfismo**: Permite tratar objetos de diferentes clases de forma uniforme
- **Seguridad**: Verifica tipos antes de usar métodos específicos
- **Herencia**: También verifica clases padre
- **Flexibilidad**: Permite código que funciona con múltiples tipos

### En el mundo real:
En un sistema de formas, isinstance(figura, Circulo) permite verificar si una figura es un círculo antes de usar métodos específicos como radio. Sin isinstance(), tendrías que usar type() que no funciona con herencia.`,
      keyPoints: [
        'isinstance(objeto, Clase) verifica el tipo',
        'También verifica clases padre',
        'Útil para polimorfismo',
        'Más recomendado que type() == Clase'
      ],
      realWorldExample: 'En un sistema de empleados, isinstance(empleado, Gerente) permite verificar si un empleado es gerente antes de usar métodos específicos como aprobar_vacaciones().'
    },
    theory: `# Verificar Tipos con isinstance()

## ¿Qué es isinstance()?

**isinstance()** es una función que verifica si un objeto es instancia de una clase específica, incluyendo clases padre.

## Sintaxis

\`\`\`python
isinstance(objeto, Clase)
\`\`\`

## Características

### 1. Verificación de Tipo
Verifica si un objeto es instancia de una clase:
\`\`\`python
if isinstance(animal, Perro):
    return f"Perro llamado {animal.nombre}"
\`\`\`

### 2. Herencia
También verifica clases padre:
\`\`\`python
isinstance(perro, Animal)  # True (Perro hereda de Animal)
\`\`\`

### 3. Múltiples Clases
Puedes verificar múltiples clases:
\`\`\`python
isinstance(obj, (Clase1, Clase2))  # True si es instancia de cualquiera
\`\`\`

## Ventajas

- **Polimorfismo**: Permite tratar objetos de diferentes clases uniformemente
- **Seguridad**: Verifica tipos antes de usar métodos específicos
- **Herencia**: Funciona correctamente con herencia`,
    tips: [
      'isinstance(objeto, Clase) verifica el tipo',
      'También verifica clases padre',
      'Útil para polimorfismo',
      'Más recomendado que type() == Clase',
      'Puedes verificar múltiples clases con una tupla'
    ],
    advice: [
      'Practica usando isinstance() para verificar tipos',
      'Experimenta con herencia y isinstance()',
      'Aprende a usar isinstance() con polimorfismo',
      'Practica verificando múltiples tipos'
    ]
  },
  {
    id: 16,
    title: 'Polimorfismo con herencia',
    description: 'Aprende a usar polimorfismo: diferentes clases pueden tener métodos con el mismo nombre pero comportamiento diferente.',
    initialCode: `# Define clase base Forma
class Forma:
    def area(self):
        return 0

# Define clases Circulo y Rectangulo
class Circulo(Forma):
    def __init__(self, radio):
        self.radio = radio
    
    def area(self):
        # Completa: devuelve el área del círculo (π * radio²)
        # Usa: 3.14159 como aproximación de π
        pass

class Rectangulo(Forma):
    def __init__(self, ancho, alto):
        self.ancho = ancho
        self.alto = alto
    
    def area(self):
        # Completa: devuelve el área del rectángulo (ancho * alto)
        pass

# Función polimórfica
def calcular_areas(formas):
    # Completa: calcula y devuelve la suma de todas las áreas
    # Usa: total = 0 y for forma in formas: total += forma.area()
    pass

# Prueba el polimorfismo
formas = [Circulo(5), Rectangulo(4, 6), Circulo(3)]
print(f"Área total: {calcular_areas(formas)}")`,
    solution: `# Define clase base Forma
class Forma:
    def area(self):
        return 0

# Define clases Circulo y Rectangulo
class Circulo(Forma):
    def __init__(self, radio):
        self.radio = radio
    
    def area(self):
        # Completa: devuelve el área del círculo (π * radio²)
        # Usa: 3.14159 como aproximación de π
        return 3.14159 * self.radio ** 2

class Rectangulo(Forma):
    def __init__(self, ancho, alto):
        self.ancho = ancho
        self.alto = alto
    
    def area(self):
        # Completa: devuelve el área del rectángulo (ancho * alto)
        return self.ancho * self.alto

# Función polimórfica
def calcular_areas(formas):
    # Completa: calcula y devuelve la suma de todas las áreas
    # Usa: total = 0 y for forma in formas: total += forma.area()
    total = 0
    for forma in formas:
        total += forma.area()
    return total

# Prueba el polimorfismo
formas = [Circulo(5), Rectangulo(4, 6), Circulo(3)]
print(f"Área total: {calcular_areas(formas)}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Polimorfismo',
        input: {},
        expectedOutput: 'Área total: 120.27455',
        description: 'Verifica que el polimorfismo funcione correctamente'
      }
    ],
    hints: [
      'Polimorfismo: mismo método, diferente comportamiento',
      'Todas las clases tienen area() pero lo calculan diferente',
      'Puedes tratar objetos de diferentes clases de forma uniforme',
      'La función calcular_areas() funciona con cualquier Forma'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué polimorfismo?',
      content: `El **polimorfismo** permite que diferentes clases tengan métodos con el mismo nombre pero comportamiento diferente, permitiendo tratar objetos de diferentes clases de forma uniforme.

### ¿Por qué es poderoso?
- **Uniformidad**: Trata objetos diferentes de forma uniforme
- **Flexibilidad**: Agrega nuevas clases sin modificar código existente
- **Abstracción**: Oculta detalles de implementación
- **Extensibilidad**: Fácil agregar nuevos tipos

### En el mundo real:
En un sistema de formas, todas las formas tienen area(), pero Circulo y Rectangulo lo calculan diferente. Sin polimorfismo, necesitarías funciones separadas para cada tipo de forma.`,
      keyPoints: [
        'Polimorfismo: mismo método, diferente comportamiento',
        'Permite tratar objetos diferentes de forma uniforme',
        'Facilita agregar nuevos tipos sin modificar código',
        'Esencial para diseño orientado a objetos'
      ],
      realWorldExample: 'En un sistema de empleados, todos tienen calcular_pago(), pero Empleado, Gerente y Desarrollador lo calculan diferente. El código que calcula pagos funciona con cualquier tipo.'
    },
    theory: `# Polimorfismo con Herencia

## ¿Qué es el Polimorfismo?

El **polimorfismo** permite que diferentes clases tengan métodos con el mismo nombre pero comportamiento diferente.

## Sintaxis

\`\`\`python
class Forma:
    def area(self):
        return 0

class Circulo(Forma):
    def area(self):
        return 3.14159 * self.radio ** 2

class Rectangulo(Forma):
    def area(self):
        return self.ancho * self.alto
\`\`\`

## Características

### 1. Mismo Nombre, Diferente Comportamiento
Cada clase implementa el método diferente:
\`\`\`python
def area(self):  # Mismo nombre
    # Diferente implementación en cada clase
\`\`\`

### 2. Tratamiento Uniforme
Puedes tratar objetos diferentes de forma uniforme:
\`\`\`python
for forma in formas:
    total += forma.area()  # Funciona con cualquier Forma
\`\`\`

### 3. Extensibilidad
Fácil agregar nuevos tipos:
\`\`\`python
class Triangulo(Forma):
    def area(self):
        return 0.5 * self.base * self.altura
\`\`\`

## Ventajas

- **Uniformidad**: Trata objetos diferentes de forma uniforme
- **Flexibilidad**: Agrega nuevos tipos sin modificar código
- **Abstracción**: Oculta detalles de implementación`,
    tips: [
      'Polimorfismo: mismo método, diferente comportamiento',
      'Permite tratar objetos diferentes de forma uniforme',
      'Facilita agregar nuevos tipos',
      'Esencial para diseño orientado a objetos',
      'Los métodos deben tener la misma firma'
    ],
    advice: [
      'Practica creando jerarquías con polimorfismo',
      'Experimenta tratando objetos diferentes de forma uniforme',
      'Aprende a diseñar interfaces comunes',
      'Practica agregando nuevos tipos sin modificar código existente'
    ]
  },
  // ========== POO AVANZADO (17-30) ==========
  {
    id: 17,
    title: 'Método __str__ para representación legible',
    description: 'Aprende a implementar __str__ para crear representaciones legibles de objetos.',
    initialCode: `# Define clase Producto
class Producto:
    def __init__(self, nombre, precio):
        self.nombre = nombre
        self.precio = precio
    
    def __str__(self):
        # Completa: devuelve un string con nombre y precio en formato "nombre - $precio"
        # Usa: f"{self.nombre} - $" + str(self.precio)
        pass

# Crea un producto y prueba __str__
producto = Producto("Laptop", 999.99)
print(str(producto))
print(producto)  # print() usa __str__ automáticamente`,
    solution: `# Define clase Producto
class Producto:
    def __init__(self, nombre, precio):
        self.nombre = nombre
        self.precio = precio
    
    def __str__(self):
        # Completa: devuelve un string con nombre y precio en formato "nombre - $precio"
        # Usa: f"{self.nombre} - $" + str(self.precio)
        return f"{self.nombre} - $" + str(self.precio)

# Crea un producto y prueba __str__
producto = Producto("Laptop", 999.99)
print(str(producto))
print(producto)  # print() usa __str__ automáticamente`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __str__ para representación legible',
        input: {},
        expectedOutput: ['Laptop - $999.99', 'Laptop - $999.99'],
        description: 'Verifica que __str__ funcione correctamente'
      }
    ],
    hints: [
      '__str__() devuelve una representación legible del objeto',
      'print() y str() usan __str__ automáticamente',
      'Debe devolver un string',
      'Útil para mostrar información al usuario'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __str__?',
      content: `**__str__** permite crear representaciones legibles de objetos que se muestran cuando usas print() o str(), mejorando la experiencia de usuario y debugging.

### ¿Por qué es importante?
- **Legibilidad**: Representaciones claras y útiles
- **Debugging**: Facilita ver el estado de objetos
- **UX**: Mejora la experiencia del usuario
- **Estándar**: Convención estándar de Python

### En el mundo real:
En un sistema de productos, print(producto) muestra "Laptop - $999.99" en lugar de "<Producto object at 0x...>". Sin __str__, verías información técnica inútil.`,
      keyPoints: [
        '__str__() devuelve representación legible',
        'print() y str() usan __str__ automáticamente',
        'Debe devolver un string',
        'Útil para mostrar información al usuario'
      ],
      realWorldExample: 'En un sistema de empleados, print(empleado) muestra "Juan Pérez - Desarrollador" en lugar de información técnica.'
    },
    theory: `# Método __str__

## ¿Qué es __str__?

**__str__** es un método especial que devuelve una representación legible del objeto como string.

## Sintaxis

\`\`\`python
class Producto:
    def __str__(self):
        return f"{self.nombre} - $" + str(self.precio)
\`\`\`

## Características

### 1. Llamado Automáticamente
Se llama cuando usas \`print()\` o \`str()\`:
\`\`\`python
print(producto)  # Llama __str__()
str(producto)    # Llama __str__()
\`\`\`

### 2. Debe Devolver String
Siempre debe devolver un string:
\`\`\`python
return f"{self.nombre} - $" + str(self.precio)
\`\`\`

### 3. Representación Legible
Debe ser legible para humanos:
\`\`\`python
# Bueno: "Laptop - $999.99"
# Malo: "<Producto object at 0x...>"
\`\`\`

## Ventajas

- **Legibilidad**: Representaciones claras
- **Debugging**: Facilita ver el estado
- **UX**: Mejora la experiencia del usuario`,
    tips: [
      '__str__() devuelve representación legible',
      'print() y str() usan __str__ automáticamente',
      'Debe devolver un string',
      'Útil para mostrar información al usuario',
      'Hazlo legible para humanos'
    ],
    advice: [
      'Practica implementando __str__ en tus clases',
      'Experimenta con diferentes formatos de representación',
      'Aprende a hacer representaciones claras y útiles',
      'Practica con objetos complejos'
    ]
  },
  {
    id: 18,
    title: 'Método __repr__ para representación técnica',
    description: 'Aprende a implementar __repr__ para crear representaciones técnicas de objetos.',
    initialCode: `# Define clase Punto
class Punto:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def __repr__(self):
        # Completa: devuelve "Punto(x, y)" para que pueda recrearse
        pass

# Crea un punto y prueba __repr__
p = Punto(3, 4)
print(repr(p))
print(p)  # Si no hay __str__, usa __repr__`,
    solution: `# Define clase Punto
class Punto:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def __repr__(self):
        # Completa: devuelve "Punto(x, y)" para que pueda recrearse
        return f"Punto({self.x}, {self.y})"

# Crea un punto y prueba __repr__
p = Punto(3, 4)
print(repr(p))
print(p)  # Si no hay __str__, usa __repr__`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __repr__ para representación técnica',
        input: {},
        expectedOutput: ['Punto(3, 4)', 'Punto(3, 4)'],
        description: 'Verifica que __repr__ funcione correctamente'
      }
    ],
    hints: [
      '__repr__() devuelve representación técnica',
      'repr() usa __repr__ automáticamente',
      'Idealmente debería poder recrear el objeto',
      'Útil para debugging y desarrollo'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __repr__?',
      content: `**__repr__** permite crear representaciones técnicas de objetos que son útiles para debugging y desarrollo, idealmente permitiendo recrear el objeto.

### ¿Por qué es importante?
- **Debugging**: Representaciones técnicas útiles para desarrolladores
- **Recreación**: Idealmente permite recrear el objeto
- **Estándar**: Convención estándar de Python
- **Desarrollo**: Facilita el desarrollo y testing

### En el mundo real:
En un sistema de coordenadas, repr(punto) muestra "Punto(3, 4)" que puede usarse para recrear el objeto. Sin __repr__, verías información menos útil.`,
      keyPoints: [
        '__repr__() devuelve representación técnica',
        'repr() usa __repr__ automáticamente',
        'Idealmente debería poder recrear el objeto',
        'Útil para debugging y desarrollo'
      ],
      realWorldExample: 'En un sistema de fechas, repr(fecha) muestra "Fecha(2024, 1, 15)" que puede usarse para recrear el objeto.'
    },
    theory: `# Método __repr__

## ¿Qué es __repr__?

**__repr__** es un método especial que devuelve una representación técnica del objeto, idealmente permitiendo recrearlo.

## Sintaxis

\`\`\`python
class Punto:
    def __repr__(self):
        return f"Punto({self.x}, {self.y})"
\`\`\`

## Características

### 1. Llamado por repr()
Se llama cuando usas \`repr()\`:
\`\`\`python
repr(punto)  # Llama __repr__()
\`\`\`

### 2. Fallback para __str__
Si no hay \`__str__\`, se usa \`__repr__\`:
\`\`\`python
print(punto)  # Usa __repr__() si no hay __str__()
\`\`\`

### 3. Representación Técnica
Idealmente permite recrear el objeto:
\`\`\`python
# Ideal: "Punto(3, 4)" puede usarse para recrear
\`\`\`

## Ventajas

- **Debugging**: Representaciones técnicas útiles
- **Recreación**: Permite recrear objetos
- **Desarrollo**: Facilita desarrollo y testing`,
    tips: [
      '__repr__() devuelve representación técnica',
      'repr() usa __repr__ automáticamente',
      'Idealmente debería poder recrear el objeto',
      'Útil para debugging y desarrollo',
      'Si no hay __str__, se usa __repr__'
    ],
    advice: [
      'Practica implementando __repr__ en tus clases',
      'Experimenta con representaciones que permitan recrear objetos',
      'Aprende la diferencia entre __str__ y __repr__',
      'Practica con objetos complejos'
    ]
  },
  {
    id: 19,
    title: 'Método __len__ para objetos con tamaño',
    description: 'Aprende a implementar __len__ para que tus objetos funcionen con len().',
    initialCode: `# Define clase ListaPersonalizada
class ListaPersonalizada:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        self.elementos.append(elemento)
    
    def __len__(self):
        # Completa: devuelve la cantidad de elementos
        pass

# Crea una lista personalizada y prueba __len__
lista = ListaPersonalizada()
lista.agregar(1)
lista.agregar(2)
lista.agregar(3)
print(f"Longitud: {len(lista)}")`,
    solution: `# Define clase ListaPersonalizada
class ListaPersonalizada:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        self.elementos.append(elemento)
    
    def __len__(self):
        # Completa: devuelve la cantidad de elementos
        return len(self.elementos)

# Crea una lista personalizada y prueba __len__
lista = ListaPersonalizada()
lista.agregar(1)
lista.agregar(2)
lista.agregar(3)
print(f"Longitud: {len(lista)}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __len__ para objetos con tamaño',
        input: {},
        expectedOutput: 'Longitud: 3',
        description: 'Verifica que __len__ funcione correctamente'
      }
    ],
    hints: [
      '__len__() devuelve el tamaño del objeto',
      'len() usa __len__ automáticamente',
      'Debe devolver un entero',
      'Útil para objetos que tienen tamaño'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __len__?',
      content: `**__len__** permite que tus objetos funcionen con la función len(), haciendo que se comporten como contenedores estándar de Python.

### ¿Por qué es útil?
- **Consistencia**: Funciona con len() como tipos built-in
- **Conveniencia**: Código más legible y estándar
- **Integración**: Compatible con código que espera len()
- **Estándar**: Convención estándar de Python

### En el mundo real:
En un sistema de inventario, len(inventario) devuelve la cantidad de productos. Sin __len__, tendrías que usar inventario.cantidad() que es menos estándar.`,
      keyPoints: [
        '__len__() devuelve el tamaño del objeto',
        'len() usa __len__ automáticamente',
        'Debe devolver un entero',
        'Útil para objetos que tienen tamaño'
      ],
      realWorldExample: 'En un sistema de mensajería, len(buzon) devuelve la cantidad de mensajes, haciendo el código más legible.'
    },
    theory: `# Método __len__

## ¿Qué es __len__?

**__len__** es un método especial que devuelve el tamaño del objeto, permitiendo usar \`len()\` con tus objetos.

## Sintaxis

\`\`\`python
class ListaPersonalizada:
    def __len__(self):
        return len(self.elementos)
\`\`\`

## Características

### 1. Llamado por len()
Se llama cuando usas \`len()\`:
\`\`\`python
len(lista)  # Llama __len__()
\`\`\`

### 2. Debe Devolver Entero
Siempre debe devolver un entero:
\`\`\`python
return len(self.elementos)  # Entero
\`\`\`

### 3. Objetos con Tamaño
Útil para objetos que tienen tamaño:
\`\`\`python
# Listas, colecciones, contenedores, etc.
\`\`\`

## Ventajas

- **Consistencia**: Funciona con len() como tipos built-in
- **Conveniencia**: Código más legible
- **Integración**: Compatible con código estándar`,
    tips: [
      '__len__() devuelve el tamaño del objeto',
      'len() usa __len__ automáticamente',
      'Debe devolver un entero',
      'Útil para objetos que tienen tamaño',
      'Hazlo consistente con tipos built-in'
    ],
    advice: [
      'Practica implementando __len__ en tus clases',
      'Experimenta con diferentes tipos de contenedores',
      'Aprende a hacer objetos compatibles con len()',
      'Practica con objetos que tienen tamaño variable'
    ]
  },
  {
    id: 20,
    title: 'Método __add__ para operador +',
    description: 'Aprende a implementar __add__ para que tus objetos puedan usar el operador +.',
    initialCode: `# Define clase Vector
class Vector:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def __add__(self, otro):
        # Completa: devuelve un nuevo Vector con la suma de componentes
        # Usa: return Vector(self.x + otro.x, self.y + otro.y)
        pass
    
    def __str__(self):
        return f"Vector({self.x}, {self.y})"

# Crea vectores y prueba __add__
v1 = Vector(1, 2)
v2 = Vector(3, 4)
v3 = v1 + v2
print(v3)`,
    solution: `# Define clase Vector
class Vector:
    def __init__(self, x, y):
        self.x = x
        self.y = y
    
    def __add__(self, otro):
        # Completa: devuelve un nuevo Vector con la suma de componentes
        # Usa: return Vector(self.x + otro.x, self.y + otro.y)
        return Vector(self.x + otro.x, self.y + otro.y)
    
    def __str__(self):
        return f"Vector({self.x}, {self.y})"

# Crea vectores y prueba __add__
v1 = Vector(1, 2)
v2 = Vector(3, 4)
v3 = v1 + v2
print(v3)`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __add__ para operador +',
        input: {},
        expectedOutput: 'Vector(4, 6)',
        description: 'Verifica que __add__ funcione correctamente'
      }
    ],
    hints: [
      '__add__(self, otro) implementa el operador +',
      'v1 + v2 llama v1.__add__(v2)',
      'Debe devolver un nuevo objeto',
      'Útil para operaciones matemáticas'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __add__?',
      content: `**__add__** permite que tus objetos usen el operador + de forma natural, haciendo el código más legible y matemáticamente intuitivo.

### ¿Por qué es poderoso?
- **Naturalidad**: Código más legible y matemático
- **Intuitividad**: Operaciones matemáticas naturales
- **Consistencia**: Funciona como tipos built-in
- **Expresividad**: Código más expresivo

### En el mundo real:
En un sistema de gráficos, vector1 + vector2 suma vectores de forma natural. Sin __add__, tendrías que usar vector1.sumar(vector2) que es menos intuitivo.`,
      keyPoints: [
        '__add__(self, otro) implementa el operador +',
        'v1 + v2 llama v1.__add__(v2)',
        'Debe devolver un nuevo objeto',
        'Útil para operaciones matemáticas'
      ],
      realWorldExample: 'En un sistema de dinero, dinero1 + dinero2 suma cantidades de forma natural, haciendo el código más legible.'
    },
    theory: `# Método __add__

## ¿Qué es __add__?

**__add__** es un método especial que implementa el operador \`+\`, permitiendo usar \`+\` con tus objetos.

## Sintaxis

\`\`\`python
class Vector:
    def __add__(self, otro):
        return Vector(self.x + otro.x, self.y + otro.y)
\`\`\`

## Características

### 1. Operador +
Se llama cuando usas \`+\`:
\`\`\`python
v1 + v2  # Llama v1.__add__(v2)
\`\`\`

### 2. Nuevo Objeto
Generalmente devuelve un nuevo objeto:
\`\`\`python
return Vector(self.x + otro.x, self.y + otro.y)
\`\`\`

### 3. Operaciones Matemáticas
Útil para operaciones matemáticas:
\`\`\`python
# Vectores, matrices, números complejos, etc.
\`\`\`

## Ventajas

- **Naturalidad**: Código más legible y matemático
- **Intuitividad**: Operaciones naturales
- **Consistencia**: Funciona como tipos built-in`,
    tips: [
      '__add__(self, otro) implementa el operador +',
      'v1 + v2 llama v1.__add__(v2)',
      'Debe devolver un nuevo objeto',
      'Útil para operaciones matemáticas',
      'Puedes implementar otros operadores similares'
    ],
    advice: [
      'Practica implementando __add__ en tus clases',
      'Experimenta con diferentes tipos de operaciones',
      'Aprende a implementar otros operadores (__sub__, __mul__, etc.)',
      'Practica con objetos matemáticos'
    ]
  },
  {
    id: 21,
    title: 'Método __eq__ para comparación ==',
    description: 'Aprende a implementar __eq__ para que tus objetos puedan compararse con ==.',
    initialCode: `# Define clase Persona
class Persona:
    def __init__(self, nombre, edad):
        self.nombre = nombre
        self.edad = edad
    
    def __eq__(self, otro):
        # Completa: devuelve True si nombre y edad son iguales
        # Usa: isinstance(otro, Persona) para verificar tipo
        pass

# Crea personas y prueba __eq__
p1 = Persona("Juan", 25)
p2 = Persona("Juan", 25)
p3 = Persona("María", 30)
print(f"p1 == p2: {p1 == p2}")
print(f"p1 == p3: {p1 == p3}")`,
    solution: `# Define clase Persona
class Persona:
    def __init__(self, nombre, edad):
        self.nombre = nombre
        self.edad = edad
    
    def __eq__(self, otro):
        # Completa: devuelve True si nombre y edad son iguales
        # Usa: isinstance(otro, Persona) para verificar tipo
        if not isinstance(otro, Persona):
            return False
        return self.nombre == otro.nombre and self.edad == otro.edad

# Crea personas y prueba __eq__
p1 = Persona("Juan", 25)
p2 = Persona("Juan", 25)
p3 = Persona("María", 30)
print(f"p1 == p2: {p1 == p2}")
print(f"p1 == p3: {p1 == p3}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __eq__ para comparación ==',
        input: {},
        expectedOutput: ['p1 == p2: True', 'p1 == p3: False'],
        description: 'Verifica que __eq__ funcione correctamente'
      }
    ],
    hints: [
      '__eq__(self, otro) implementa el operador ==',
      'p1 == p2 llama p1.__eq__(p2)',
      'Debe devolver True o False',
      'Verifica el tipo con isinstance()'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __eq__?',
      content: `**__eq__** permite que tus objetos se comparen con == de forma lógica, definiendo qué significa que dos objetos sean iguales.

### ¿Por qué es esencial?
- **Comparación lógica**: Define qué significa igualdad
- **Consistencia**: Funciona como tipos built-in
- **Testing**: Facilita comparaciones en tests
- **Lógica de negocio**: Permite comparaciones significativas

### En el mundo real:
En un sistema de usuarios, usuario1 == usuario2 compara por ID o email. Sin __eq__, Python compararía por identidad de objeto (dirección de memoria), que no es útil.`,
      keyPoints: [
        '__eq__(self, otro) implementa el operador ==',
        'p1 == p2 llama p1.__eq__(p2)',
        'Debe devolver True o False',
        'Define qué significa igualdad para tu clase'
      ],
      realWorldExample: 'En un sistema de productos, producto1 == producto2 compara por SKU o código, no por identidad de objeto.'
    },
    theory: `# Método __eq__

## ¿Qué es __eq__?

**__eq__** es un método especial que implementa el operador \`==\`, definiendo qué significa igualdad para tus objetos.

## Sintaxis

\`\`\`python
class Persona:
    def __eq__(self, otro):
        if not isinstance(otro, Persona):
            return False
        return self.nombre == otro.nombre and self.edad == otro.edad
\`\`\`

## Características

### 1. Operador ==
Se llama cuando usas \`==\`:
\`\`\`python
p1 == p2  # Llama p1.__eq__(p2)
\`\`\`

### 2. Debe Devolver Boolean
Siempre debe devolver True o False:
\`\`\`python
return True   # Iguales
return False  # Diferentes
\`\`\`

### 3. Verificación de Tipo
Verifica el tipo antes de comparar:
\`\`\`python
if not isinstance(otro, Persona):
    return False
\`\`\`

## Ventajas

- **Comparación lógica**: Define igualdad significativa
- **Consistencia**: Funciona como tipos built-in
- **Testing**: Facilita comparaciones en tests`,
    tips: [
      '__eq__(self, otro) implementa el operador ==',
      'p1 == p2 llama p1.__eq__(p2)',
      'Debe devolver True o False',
      'Verifica el tipo con isinstance()',
      'Define qué significa igualdad para tu clase'
    ],
    advice: [
      'Practica implementando __eq__ en tus clases',
      'Experimenta con diferentes criterios de igualdad',
      'Aprende a verificar tipos correctamente',
      'Practica con objetos complejos'
    ]
  },
  {
    id: 22,
    title: 'Implementar una Pila (Stack)',
    description: 'Aprende a implementar una estructura de datos Pila usando clases.',
    initialCode: `# Define clase Pila
class Pila:
    def __init__(self):
        # Completa: inicializa una lista vacía para almacenar elementos
        pass
    
    def apilar(self, elemento):
        # Completa: agrega elemento al final de la lista
        pass
    
    def desapilar(self):
        # Completa: elimina y devuelve el último elemento
        # Si está vacía, devuelve None
        pass
    
    def esta_vacia(self):
        # Completa: devuelve True si la pila está vacía
        pass

# Prueba la pila
pila = Pila()
pila.apilar(1)
pila.apilar(2)
pila.apilar(3)
print(f"Desapilar: {pila.desapilar()}")
print(f"¿Vacía?: {pila.esta_vacia()}")`,
    solution: `# Define clase Pila
class Pila:
    def __init__(self):
        # Completa: inicializa una lista vacía para almacenar elementos
        self.elementos = []
    
    def apilar(self, elemento):
        # Completa: agrega elemento al final de la lista
        self.elementos.append(elemento)
    
    def desapilar(self):
        # Completa: elimina y devuelve el último elemento
        # Si está vacía, devuelve None
        if self.esta_vacia():
            return None
        return self.elementos.pop()
    
    def esta_vacia(self):
        # Completa: devuelve True si la pila está vacía
        return len(self.elementos) == 0

# Prueba la pila
pila = Pila()
pila.apilar(1)
pila.apilar(2)
pila.apilar(3)
print(f"Desapilar: {pila.desapilar()}")
print(f"¿Vacía?: {pila.esta_vacia()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Operaciones de pila',
        input: {},
        expectedOutput: ['Desapilar: 3', '¿Vacía?: False'],
        description: 'Verifica que la pila funcione correctamente'
      }
    ],
    hints: [
      'Una pila es LIFO (Last In, First Out)',
      'apilar() agrega al final (append)',
      'desapilar() elimina del final (pop)',
      'Usa una lista para almacenar elementos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar una Pila?',
      content: `Una **Pila** es una estructura de datos fundamental (LIFO: Last In, First Out) que es esencial para muchos algoritmos y sistemas.

### ¿Por qué es importante?
- **Algoritmos**: Usada en muchos algoritmos (recursión, backtracking)
- **Sistemas**: Usada en sistemas (call stack, undo/redo)
- **Fundamental**: Estructura de datos básica
- **Práctica**: Excelente ejercicio de POO

### En el mundo real:
En un editor de texto, el sistema de undo/redo usa pilas. Cada acción se apila, y undo desapila la última acción. Sin pilas, sería difícil implementar esta funcionalidad.`,
      keyPoints: [
        'Pila es LIFO (Last In, First Out)',
        'apilar() agrega elementos',
        'desapilar() elimina el último elemento',
        'Útil para muchos algoritmos y sistemas'
      ],
      realWorldExample: 'En un navegador web, el historial de navegación usa una pila. Cada página visitada se apila, y el botón "atrás" desapila.'
    },
    theory: `# Implementar una Pila

## ¿Qué es una Pila?

Una **Pila** es una estructura de datos LIFO (Last In, First Out): el último elemento agregado es el primero en salir.

## Operaciones

### 1. Apilar (Push)
Agrega un elemento al final:
\`\`\`python
def apilar(self, elemento):
    self.elementos.append(elemento)
\`\`\`

### 2. Desapilar (Pop)
Elimina y devuelve el último elemento:
\`\`\`python
def desapilar(self):
    return self.elementos.pop()
\`\`\`

### 3. Verificar si está vacía
\`\`\`python
def esta_vacia(self):
    return len(self.elementos) == 0
\`\`\`

## Características

- **LIFO**: Last In, First Out
- **Acceso limitado**: Solo al último elemento
- **Eficiente**: Operaciones O(1)

## Ventajas

- **Simplicidad**: Estructura simple y eficiente
- **Algoritmos**: Usada en muchos algoritmos
- **Sistemas**: Usada en sistemas reales`,
    tips: [
      'Pila es LIFO (Last In, First Out)',
      'apilar() agrega al final (append)',
      'desapilar() elimina del final (pop)',
      'Usa una lista para almacenar elementos',
      'Verifica si está vacía antes de desapilar'
    ],
    advice: [
      'Practica implementando una pila desde cero',
      'Experimenta con diferentes operaciones',
      'Aprende sobre otras estructuras de datos',
      'Practica usando pilas en algoritmos'
    ]
  },
  {
    id: 23,
    title: 'Implementar una Cola (Queue)',
    description: 'Aprende a implementar una estructura de datos Cola usando clases.',
    initialCode: `# Define clase Cola
class Cola:
    def __init__(self):
        # Completa: inicializa una lista vacía para almacenar elementos
        pass
    
    def encolar(self, elemento):
        # Completa: agrega elemento al final de la lista
        pass
    
    def desencolar(self):
        # Completa: elimina y devuelve el primer elemento
        # Si está vacía, devuelve None
        pass
    
    def esta_vacia(self):
        # Completa: devuelve True si la cola está vacía
        pass

# Prueba la cola
cola = Cola()
cola.encolar(1)
cola.encolar(2)
cola.encolar(3)
print(f"Desencolar: {cola.desencolar()}")
print(f"¿Vacía?: {cola.esta_vacia()}")`,
    solution: `# Define clase Cola
class Cola:
    def __init__(self):
        # Completa: inicializa una lista vacía para almacenar elementos
        self.elementos = []
    
    def encolar(self, elemento):
        # Completa: agrega elemento al final de la lista
        self.elementos.append(elemento)
    
    def desencolar(self):
        # Completa: elimina y devuelve el primer elemento
        # Si está vacía, devuelve None
        if self.esta_vacia():
            return None
        return self.elementos.pop(0)
    
    def esta_vacia(self):
        # Completa: devuelve True si la cola está vacía
        return len(self.elementos) == 0

# Prueba la cola
cola = Cola()
cola.encolar(1)
cola.encolar(2)
cola.encolar(3)
print(f"Desencolar: {cola.desencolar()}")
print(f"¿Vacía?: {cola.esta_vacia()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Operaciones de cola',
        input: {},
        expectedOutput: ['Desencolar: 1', '¿Vacía?: False'],
        description: 'Verifica que la cola funcione correctamente'
      }
    ],
    hints: [
      'Una cola es FIFO (First In, First Out)',
      'encolar() agrega al final (append)',
      'desencolar() elimina del inicio (pop(0))',
      'Usa una lista para almacenar elementos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar una Cola?',
      content: `Una **Cola** es una estructura de datos fundamental (FIFO: First In, First Out) que es esencial para muchos sistemas y algoritmos.

### ¿Por qué es importante?
- **Sistemas**: Usada en sistemas (procesamiento de tareas, impresión)
- **Algoritmos**: Usada en algoritmos (BFS, scheduling)
- **Fundamental**: Estructura de datos básica
- **Práctica**: Excelente ejercicio de POO

### En el mundo real:
En un sistema de impresión, las tareas de impresión se encolan y se procesan en orden. Sin colas, sería difícil manejar múltiples tareas de forma ordenada.`,
      keyPoints: [
        'Cola es FIFO (First In, First Out)',
        'encolar() agrega elementos',
        'desencolar() elimina el primer elemento',
        'Útil para muchos sistemas y algoritmos'
      ],
      realWorldExample: 'En un sistema de atención al cliente, los clientes se encolan y se atienden en orden de llegada.'
    },
    theory: `# Implementar una Cola

## ¿Qué es una Cola?

Una **Cola** es una estructura de datos FIFO (First In, First Out): el primer elemento agregado es el primero en salir.

## Operaciones

### 1. Encolar (Enqueue)
Agrega un elemento al final:
\`\`\`python
def encolar(self, elemento):
    self.elementos.append(elemento)
\`\`\`

### 2. Desencolar (Dequeue)
Elimina y devuelve el primer elemento:
\`\`\`python
def desencolar(self):
    return self.elementos.pop(0)
\`\`\`

### 3. Verificar si está vacía
\`\`\`python
def esta_vacia(self):
    return len(self.elementos) == 0
\`\`\`

## Características

- **FIFO**: First In, First Out
- **Acceso limitado**: Solo al primer elemento
- **Eficiente**: Operaciones O(1) con implementación adecuada

## Ventajas

- **Simplicidad**: Estructura simple y eficiente
- **Sistemas**: Usada en sistemas reales
- **Algoritmos**: Usada en algoritmos importantes`,
    tips: [
      'Cola es FIFO (First In, First Out)',
      'encolar() agrega al final (append)',
      'desencolar() elimina del inicio (pop(0))',
      'Usa una lista para almacenar elementos',
      'Verifica si está vacía antes de desencolar'
    ],
    advice: [
      'Practica implementando una cola desde cero',
      'Experimenta con diferentes operaciones',
      'Aprende sobre otras estructuras de datos',
      'Practica usando colas en sistemas'
    ]
  },
  {
    id: 24,
    title: 'Encapsulación con atributos privados',
    description: 'Aprende a usar convenciones de nombres para crear atributos "privados" en Python.',
    initialCode: `# Define clase CuentaBancaria con encapsulación
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial=0):
        self.titular = titular
        self._saldo = saldo_inicial  # _ indica "protegido"
    
    def depositar(self, monto):
        # Completa: valida que monto > 0 y suma a self._saldo
        pass
    
    def retirar(self, monto):
        # Completa: valida que haya suficiente saldo y resta de self._saldo
        pass
    
    def obtener_saldo(self):
        # Completa: devuelve self._saldo
        pass

# Prueba la encapsulación
cuenta = CuentaBancaria("Juan", 100)
cuenta.depositar(50)
cuenta.retirar(30)
print(f"Saldo: {cuenta.obtener_saldo()}")`,
    solution: `# Define clase CuentaBancaria con encapsulación
class CuentaBancaria:
    def __init__(self, titular, saldo_inicial=0):
        self.titular = titular
        self._saldo = saldo_inicial  # _ indica "protegido"
    
    def depositar(self, monto):
        # Completa: valida que monto > 0 y suma a self._saldo
        if monto > 0:
            self._saldo += monto
    
    def retirar(self, monto):
        # Completa: valida que haya suficiente saldo y resta de self._saldo
        if monto > 0 and self._saldo >= monto:
            self._saldo -= monto
    
    def obtener_saldo(self):
        # Completa: devuelve self._saldo
        return self._saldo

# Prueba la encapsulación
cuenta = CuentaBancaria("Juan", 100)
cuenta.depositar(50)
cuenta.retirar(30)
print(f"Saldo: {cuenta.obtener_saldo()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Encapsulación con atributos privados',
        input: {},
        expectedOutput: 'Saldo: 120',
        description: 'Verifica que la encapsulación funcione correctamente'
      }
    ],
    hints: [
      '_nombre indica atributo "protegido" (convención)',
      'Los atributos protegidos no deben accederse directamente',
      'Usa métodos para acceder y modificar atributos protegidos',
      'Python no tiene atributos realmente privados, solo convenciones'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar encapsulación?',
      content: `La **encapsulación** protege los datos internos del objeto, permitiendo acceso controlado y manteniendo la integridad de los datos.

### ¿Por qué es esencial?
- **Protección**: Protege datos internos de acceso directo
- **Integridad**: Mantiene el objeto en estados válidos
- **Control**: Permite validar antes de modificar
- **Mantenibilidad**: Facilita cambios internos sin afectar código externo

### En el mundo real:
En un sistema bancario, el saldo está protegido y solo se modifica mediante métodos que validan. Sin encapsulación, cualquier código podría modificar el saldo directamente, causando errores.`,
      keyPoints: [
        '_nombre indica atributo protegido (convención)',
        'Los atributos protegidos no deben accederse directamente',
        'Usa métodos para acceder y modificar',
        'Python usa convenciones, no privacidad real'
      ],
      realWorldExample: 'En un sistema de inventario, el stock está protegido y solo se modifica mediante métodos que validan disponibilidad.'
    },
    theory: `# Encapsulación con Atributos Privados

## ¿Qué es la Encapsulación?

La **encapsulación** es el principio de ocultar los detalles internos de una clase y proporcionar acceso controlado a través de métodos.

## Convenciones en Python

### 1. Atributos Públicos
Accesibles desde cualquier lugar:
\`\`\`python
self.titular = titular  # Público
\`\`\`

### 2. Atributos Protegidos
Convención: no acceder directamente (un guión bajo):
\`\`\`python
self._saldo = saldo_inicial  # Protegido
\`\`\`

### 3. Atributos Privados
Convención: muy privado (doble guión bajo):
\`\`\`python
self.__secreto = valor  # Privado (name mangling)
\`\`\`

## Ventajas

- **Protección**: Protege datos internos
- **Integridad**: Mantiene estados válidos
- **Control**: Permite validación
- **Mantenibilidad**: Facilita cambios`,
    tips: [
      '_nombre indica atributo protegido',
      'No accedas directamente a atributos protegidos',
      'Usa métodos para acceder y modificar',
      'Python usa convenciones, no privacidad real',
      'Respetar convenciones hace el código más mantenible'
    ],
    advice: [
      'Practica usando atributos protegidos',
      'Experimenta con diferentes niveles de encapsulación',
      'Aprende cuándo usar cada convención',
      'Practica diseñando interfaces públicas'
    ]
  },
  {
    id: 25,
    title: 'Propiedades con @property',
    description: 'Aprende a usar @property para crear atributos calculados y controlar acceso.',
    initialCode: `# Define clase Rectangulo con propiedades
class Rectangulo:
    def __init__(self, ancho, alto):
        self._ancho = ancho
        self._alto = alto
    
    @property
    def area(self):
        # Completa: devuelve el área (ancho * alto)
        pass
    
    @property
    def perimetro(self):
        # Completa: devuelve el perímetro (2 * ancho + 2 * alto)
        pass

# Prueba las propiedades
rect = Rectangulo(5, 3)
print(f"Área: {rect.area}")
print(f"Perímetro: {rect.perimetro}")`,
    solution: `# Define clase Rectangulo con propiedades
class Rectangulo:
    def __init__(self, ancho, alto):
        self._ancho = ancho
        self._alto = alto
    
    @property
    def area(self):
        # Completa: devuelve el área (ancho * alto)
        return self._ancho * self._alto
    
    @property
    def perimetro(self):
        # Completa: devuelve el perímetro (2 * ancho + 2 * alto)
        return 2 * self._ancho + 2 * self._alto

# Prueba las propiedades
rect = Rectangulo(5, 3)
print(f"Área: {rect.area}")
print(f"Perímetro: {rect.perimetro}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Propiedades con @property',
        input: {},
        expectedOutput: ['Área: 15', 'Perímetro: 16'],
        description: 'Verifica que las propiedades funcionen correctamente'
      }
    ],
    hints: [
      '@property convierte un método en una propiedad',
      'Puedes acceder a propiedades como atributos (sin paréntesis)',
      'Útil para valores calculados',
      'Las propiedades son de solo lectura por defecto'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar @property?',
      content: `**@property** permite crear atributos calculados que se comportan como atributos normales pero se calculan dinámicamente, mejorando la API de la clase.

### ¿Por qué es útil?
- **API limpia**: Acceso como atributo, cálculo como método
- **Cálculos dinámicos**: Valores que se calculan cuando se acceden
- **Encapsulación**: Oculta la implementación del cálculo
- **Consistencia**: Comportamiento consistente con atributos

### En el mundo real:
En un sistema de formas, rectangulo.area calcula el área dinámicamente cuando se accede. Sin @property, tendrías que usar rectangulo.calcular_area() que es menos intuitivo.`,
      keyPoints: [
        '@property convierte método en propiedad',
        'Acceso como atributo (sin paréntesis)',
        'Útil para valores calculados',
        'Mejora la API de la clase'
      ],
      realWorldExample: 'En un sistema de empleados, empleado.salario_anual calcula el salario anual basándose en el salario mensual cuando se accede.'
    },
    theory: `# Propiedades con @property

## ¿Qué es @property?

**@property** es un decorador que convierte un método en una propiedad, permitiendo acceso como atributo pero con cálculo dinámico.

## Sintaxis

\`\`\`python
class Rectangulo:
    @property
    def area(self):
        return self._ancho * self._alto
\`\`\`

## Características

### 1. Acceso como Atributo
Se accede sin paréntesis:
\`\`\`python
rect.area  # No rect.area()
\`\`\`

### 2. Cálculo Dinámico
Se calcula cada vez que se accede:
\`\`\`python
return self._ancho * self._alto  # Se calcula cada vez
\`\`\`

### 3. Solo Lectura por Defecto
No se puede asignar directamente:
\`\`\`python
rect.area = 20  # Error (necesitas setter)
\`\`\`

## Ventajas

- **API limpia**: Acceso como atributo
- **Cálculos dinámicos**: Valores calculados cuando se necesitan
- **Encapsulación**: Oculta implementación`,
    tips: [
      '@property convierte método en propiedad',
      'Acceso como atributo (sin paréntesis)',
      'Útil para valores calculados',
      'Solo lectura por defecto',
      'Puedes agregar setters con @propiedad.setter'
    ],
    advice: [
      'Practica usando @property para valores calculados',
      'Experimenta con propiedades de solo lectura',
      'Aprende a crear setters cuando sea necesario',
      'Practica mejorando APIs con propiedades'
    ]
  },
  {
    id: 26,
    title: 'Método __getitem__ para indexación',
    description: 'Aprende a implementar __getitem__ para que tus objetos puedan usar indexación [].',
    initialCode: `# Define clase ListaPersonalizada
class ListaPersonalizada:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        self.elementos.append(elemento)
    
    def __getitem__(self, indice):
        # Completa: devuelve el elemento en el índice
        # Si el índice está fuera de rango, devuelve None
        pass

# Prueba la indexación
lista = ListaPersonalizada()
lista.agregar(10)
lista.agregar(20)
lista.agregar(30)
print(f"Elemento en índice 1: {lista[1]}")
print(f"Elemento en índice 0: {lista[0]}")`,
    solution: `# Define clase ListaPersonalizada
class ListaPersonalizada:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        self.elementos.append(elemento)
    
    def __getitem__(self, indice):
        # Completa: devuelve el elemento en el índice
        # Si el índice está fuera de rango, devuelve None
        if 0 <= indice < len(self.elementos):
            return self.elementos[indice]
        return None

# Prueba la indexación
lista = ListaPersonalizada()
lista.agregar(10)
lista.agregar(20)
lista.agregar(30)
print(f"Elemento en índice 1: {lista[1]}")
print(f"Elemento en índice 0: {lista[0]}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __getitem__ para indexación',
        input: {},
        expectedOutput: ['Elemento en índice 1: 20', 'Elemento en índice 0: 10'],
        description: 'Verifica que __getitem__ funcione correctamente'
      }
    ],
    hints: [
      '__getitem__(self, indice) implementa la indexación []',
      'lista[indice] llama lista.__getitem__(indice)',
      'Debe devolver el elemento en el índice',
      'Verifica que el índice esté en rango'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __getitem__?',
      content: `**__getitem__** permite que tus objetos usen indexación [] como listas y diccionarios, haciendo el código más intuitivo y consistente.

### ¿Por qué es poderoso?
- **Intuitividad**: Indexación natural como tipos built-in
- **Consistencia**: Funciona como listas y diccionarios
- **Expresividad**: Código más legible
- **Integración**: Compatible con código que espera indexación

### En el mundo real:
En un sistema de inventario, inventario[0] accede al primer producto de forma natural. Sin __getitem__, tendrías que usar inventario.obtener(0) que es menos intuitivo.`,
      keyPoints: [
        '__getitem__(self, indice) implementa []',
        'lista[indice] llama lista.__getitem__(indice)',
        'Debe devolver el elemento en el índice',
        'Útil para objetos que actúan como contenedores'
      ],
      realWorldExample: 'En un sistema de mensajería, buzon[0] accede al primer mensaje de forma natural.'
    },
    theory: `# Método __getitem__

## ¿Qué es __getitem__?

**__getitem__** es un método especial que implementa la indexación \`[]\`, permitiendo acceder a elementos como en listas y diccionarios.

## Sintaxis

\`\`\`python
class ListaPersonalizada:
    def __getitem__(self, indice):
        return self.elementos[indice]
\`\`\`

## Características

### 1. Indexación []
Se llama cuando usas \`[]\`:
\`\`\`python
lista[indice]  # Llama lista.__getitem__(indice)
\`\`\`

### 2. Acceso a Elementos
Devuelve el elemento en el índice:
\`\`\`python
return self.elementos[indice]
\`\`\`

### 3. Validación
Puedes validar el índice:
\`\`\`python
if 0 <= indice < len(self.elementos):
    return self.elementos[indice]
\`\`\`

## Ventajas

- **Intuitividad**: Indexación natural
- **Consistencia**: Funciona como tipos built-in
- **Expresividad**: Código más legible`,
    tips: [
      '__getitem__(self, indice) implementa []',
      'lista[indice] llama lista.__getitem__(indice)',
      'Debe devolver el elemento en el índice',
      'Verifica que el índice esté en rango',
      'Útil para objetos que actúan como contenedores'
    ],
    advice: [
      'Practica implementando __getitem__ en tus clases',
      'Experimenta con diferentes tipos de indexación',
      'Aprende a validar índices correctamente',
      'Practica con objetos que actúan como contenedores'
    ]
  },
  {
    id: 27,
    title: 'Método __contains__ para operador in',
    description: 'Aprende a implementar __contains__ para que tus objetos funcionen con el operador in.',
    initialCode: `# Define clase ConjuntoPersonalizado
class ConjuntoPersonalizado:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        if elemento not in self.elementos:
            self.elementos.append(elemento)
    
    def __contains__(self, elemento):
        # Completa: devuelve True si elemento está en self.elementos
        pass

# Prueba el operador in
conjunto = ConjuntoPersonalizado()
conjunto.agregar(1)
conjunto.agregar(2)
conjunto.agregar(3)
print(f"¿2 está en el conjunto?: {2 in conjunto}")
print(f"¿5 está en el conjunto?: {5 in conjunto}")`,
    solution: `# Define clase ConjuntoPersonalizado
class ConjuntoPersonalizado:
    def __init__(self):
        self.elementos = []
    
    def agregar(self, elemento):
        if elemento not in self.elementos:
            self.elementos.append(elemento)
    
    def __contains__(self, elemento):
        # Completa: devuelve True si elemento está en self.elementos
        return elemento in self.elementos

# Prueba el operador in
conjunto = ConjuntoPersonalizado()
conjunto.agregar(1)
conjunto.agregar(2)
conjunto.agregar(3)
print(f"¿2 está en el conjunto?: {2 in conjunto}")
print(f"¿5 está en el conjunto?: {5 in conjunto}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: __contains__ para operador in',
        input: {},
        expectedOutput: ['¿2 está en el conjunto?: True', '¿5 está en el conjunto?: False'],
        description: 'Verifica que __contains__ funcione correctamente'
      }
    ],
    hints: [
      '__contains__(self, elemento) implementa el operador in',
      'elemento in objeto llama objeto.__contains__(elemento)',
      'Debe devolver True o False',
      'Útil para objetos que actúan como contenedores'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué implementar __contains__?',
      content: `**__contains__** permite que tus objetos funcionen con el operador \`in\`, haciendo el código más intuitivo y consistente con tipos built-in.

### ¿Por qué es útil?
- **Intuitividad**: Operador in natural como tipos built-in
- **Consistencia**: Funciona como listas y conjuntos
- **Expresividad**: Código más legible
- **Integración**: Compatible con código que espera in

### En el mundo real:
En un sistema de permisos, "admin" in usuario.permisos verifica si el usuario tiene permisos de admin de forma natural. Sin __contains__, tendrías que usar usuario.tiene_permiso("admin") que es menos intuitivo.`,
      keyPoints: [
        '__contains__(self, elemento) implementa in',
        'elemento in objeto llama objeto.__contains__(elemento)',
        'Debe devolver True o False',
        'Útil para objetos que actúan como contenedores'
      ],
      realWorldExample: 'En un sistema de tags, "python" in producto.tags verifica si el producto tiene el tag de forma natural.'
    },
    theory: `# Método __contains__

## ¿Qué es __contains__?

**__contains__** es un método especial que implementa el operador \`in\`, permitiendo verificar membresía como en listas y conjuntos.

## Sintaxis

\`\`\`python
class ConjuntoPersonalizado:
    def __contains__(self, elemento):
        return elemento in self.elementos
\`\`\`

## Características

### 1. Operador in
Se llama cuando usas \`in\`:
\`\`\`python
elemento in objeto  # Llama objeto.__contains__(elemento)
\`\`\`

### 2. Debe Devolver Boolean
Siempre debe devolver True o False:
\`\`\`python
return elemento in self.elementos  # True o False
\`\`\`

### 3. Verificación de Membresía
Verifica si un elemento está en el objeto:
\`\`\`python
return elemento in self.elementos
\`\`\`

## Ventajas

- **Intuitividad**: Operador in natural
- **Consistencia**: Funciona como tipos built-in
- **Expresividad**: Código más legible`,
    tips: [
      '__contains__(self, elemento) implementa in',
      'elemento in objeto llama objeto.__contains__(elemento)',
      'Debe devolver True o False',
      'Útil para objetos que actúan como contenedores',
      'Hazlo consistente con tipos built-in'
    ],
    advice: [
      'Practica implementando __contains__ en tus clases',
      'Experimenta con diferentes tipos de contenedores',
      'Aprende a hacer objetos compatibles con in',
      'Practica con objetos que verifican membresía'
    ]
  },
  {
    id: 28,
    title: 'Composición vs Herencia',
    description: 'Aprende la diferencia entre composición (tener) e herencia (ser) y cuándo usar cada una.',
    initialCode: `# Ejemplo de Composición: Auto "tiene" un Motor
class Motor:
    def __init__(self, potencia):
        self.potencia = potencia
    
    def arrancar(self):
        return "Motor arrancado"

class Auto:
    def __init__(self, marca, potencia_motor):
        self.marca = marca
        # Completa: crea un Motor con potencia_motor
        # Usa: self.motor = Motor(potencia_motor)
        pass
    
    def arrancar(self):
        # Completa: usa self.motor.arrancar() y devuelve el resultado
        pass

# Prueba la composición
auto = Auto("Toyota", 150)
print(auto.arrancar())`,
    solution: `# Ejemplo de Composición: Auto "tiene" un Motor
class Motor:
    def __init__(self, potencia):
        self.potencia = potencia
    
    def arrancar(self):
        return "Motor arrancado"

class Auto:
    def __init__(self, marca, potencia_motor):
        self.marca = marca
        # Completa: crea un Motor con potencia_motor
        # Usa: self.motor = Motor(potencia_motor)
        self.motor = Motor(potencia_motor)
    
    def arrancar(self):
        # Completa: usa self.motor.arrancar() y devuelve el resultado
        return self.motor.arrancar()

# Prueba la composición
auto = Auto("Toyota", 150)
print(auto.arrancar())`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Composición vs Herencia',
        input: {},
        expectedOutput: 'Motor arrancado',
        description: 'Verifica que la composición funcione correctamente'
      }
    ],
    hints: [
      'Composición: Auto "tiene" un Motor (relación tiene-a)',
      'Herencia: Perro "es" un Animal (relación es-un)',
      'Composición: crea objetos dentro de otros',
      'Usa composición cuando la relación es "tiene"'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué entender Composición vs Herencia?',
      content: `Entender la diferencia entre **Composición** (tener) e **Herencia** (ser) es esencial para diseñar clases correctamente y evitar problemas de diseño.

### ¿Por qué es crítico?
- **Diseño correcto**: Usa la relación correcta
- **Flexibilidad**: Composición es más flexible que herencia
- **Mantenibilidad**: Evita jerarquías complejas innecesarias
- **Principios**: Sigue principios SOLID

### En el mundo real:
Un Auto "tiene" un Motor (composición), no "es" un Motor (herencia). Usar herencia aquí sería incorrecto porque un auto no es un tipo de motor.`,
      keyPoints: [
        'Composición: "tiene" (has-a)',
        'Herencia: "es" (is-a)',
        'Composición es más flexible',
        'Usa composición cuando sea posible'
      ],
      realWorldExample: 'Un Empleado "tiene" una Direccion (composición), no "es" una Direccion (herencia).'
    },
    theory: `# Composición vs Herencia

## ¿Qué es Composición?

**Composición** es cuando una clase contiene una instancia de otra clase (relación "tiene").

## ¿Qué es Herencia?

**Herencia** es cuando una clase es un tipo de otra clase (relación "es").

## Comparación

### Composición (tiene)
\`\`\`python
class Auto:
    def __init__(self):
        self.motor = Motor()  # Auto "tiene" un Motor
\`\`\`

### Herencia (es)
\`\`\`python
class Perro(Animal):  # Perro "es" un Animal
    pass
\`\`\`

## Cuándo Usar Cada Una

- **Composición**: Cuando la relación es "tiene"
- **Herencia**: Cuando la relación es "es"
- **Regla**: Prefiere composición sobre herencia

## Ventajas

- **Composición**: Más flexible, menos acoplamiento
- **Herencia**: Reutiliza código, crea jerarquías`,
    tips: [
      'Composición: "tiene" (has-a)',
      'Herencia: "es" (is-a)',
      'Composición es más flexible',
      'Usa composición cuando sea posible',
      'Herencia solo cuando realmente "es" un tipo'
    ],
    advice: [
      'Practica identificando relaciones "tiene" vs "es"',
      'Experimenta con composición en tus diseños',
      'Aprende cuándo usar cada una',
      'Practica prefiriendo composición sobre herencia'
    ]
  },
  {
    id: 29,
    title: 'Clase abstracta con ABC',
    description: 'Aprende a crear clases abstractas que definen interfaces sin implementación completa.',
    initialCode: `# Importa ABC y abstractmethod
from abc import ABC, abstractmethod

# Define clase abstracta Forma
class Forma(ABC):
    @abstractmethod
    def area(self):
        # Completa: pass (no implementación)
        pass
    
    @abstractmethod
    def perimetro(self):
        # Completa: pass (no implementación)
        pass

# Define clase Circulo que hereda de Forma
class Circulo(Forma):
    def __init__(self, radio):
        self.radio = radio
    
    def area(self):
        # Completa: devuelve π * radio² (usa 3.14159)
        pass
    
    def perimetro(self):
        # Completa: devuelve 2 * π * radio
        pass

# Prueba la clase abstracta
circulo = Circulo(5)
print(f"Área: {circulo.area()}")
print(f"Perímetro: {circulo.perimetro()}")`,
    solution: `# Importa ABC y abstractmethod
from abc import ABC, abstractmethod

# Define clase abstracta Forma
class Forma(ABC):
    @abstractmethod
    def area(self):
        # Completa: pass (no implementación)
        pass
    
    @abstractmethod
    def perimetro(self):
        # Completa: pass (no implementación)
        pass

# Define clase Circulo que hereda de Forma
class Circulo(Forma):
    def __init__(self, radio):
        self.radio = radio
    
    def area(self):
        # Completa: devuelve π * radio² (usa 3.14159)
        return 3.14159 * self.radio ** 2
    
    def perimetro(self):
        # Completa: devuelve 2 * π * radio
        return 2 * 3.14159 * self.radio

# Prueba la clase abstracta
circulo = Circulo(5)
print(f"Área: {circulo.area()}")
print(f"Perímetro: {circulo.perimetro()}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Clase abstracta con ABC',
        input: {},
        expectedOutput: ['Área: 78.53975', 'Perímetro: 31.4159'],
        description: 'Verifica que las clases abstractas funcionen correctamente'
      }
    ],
    hints: [
      'ABC es Abstract Base Class',
      '@abstractmethod marca métodos que deben implementarse',
      'No puedes crear instancias de clases abstractas',
      'Las clases hijas deben implementar todos los métodos abstractos'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué usar clases abstractas?',
      content: `Las **clases abstractas** definen interfaces comunes sin implementación completa, forzando a las clases hijas a implementar métodos específicos.

### ¿Por qué es útil?
- **Interfaces**: Define contratos que las clases hijas deben cumplir
- **Consistencia**: Asegura que todas las clases hijas implementen métodos necesarios
- **Diseño**: Facilita diseño orientado a interfaces
- **Prevención**: Previene crear instancias incompletas

### En el mundo real:
En un sistema de formas, Forma define que todas las formas deben tener area() y perimetro(), pero no implementa cómo calcularlos. Sin clases abstractas, podrías crear formas incompletas.`,
      keyPoints: [
        'ABC es Abstract Base Class',
        '@abstractmethod marca métodos abstractos',
        'No puedes crear instancias de clases abstractas',
        'Las clases hijas deben implementar métodos abstractos'
      ],
      realWorldExample: 'En un sistema de empleados, Empleado define que todos los empleados deben tener calcular_pago(), pero cada tipo lo implementa diferente.'
    },
    theory: `# Clases Abstractas con ABC

## ¿Qué es una Clase Abstracta?

Una **clase abstracta** es una clase que no puede instanciarse directamente y define métodos que las clases hijas deben implementar.

## Sintaxis

\`\`\`python
from abc import ABC, abstractmethod

class Forma(ABC):
    @abstractmethod
    def area(self):
        pass
\`\`\`

## Características

### 1. Herencia de ABC
Hereda de \`ABC\`:
\`\`\`python
class Forma(ABC):
\`\`\`

### 2. Métodos Abstractos
Marca métodos con \`@abstractmethod\`:
\`\`\`python
@abstractmethod
def area(self):
    pass
\`\`\`

### 3. Implementación Obligatoria
Las clases hijas deben implementar métodos abstractos:
\`\`\`python
class Circulo(Forma):
    def area(self):
        return 3.14159 * self.radio ** 2
\`\`\`

## Ventajas

- **Interfaces**: Define contratos claros
- **Consistencia**: Asegura implementación completa
- **Diseño**: Facilita diseño orientado a interfaces`,
    tips: [
      'ABC es Abstract Base Class',
      '@abstractmethod marca métodos abstractos',
      'No puedes crear instancias de clases abstractas',
      'Las clases hijas deben implementar métodos abstractos',
      'Útil para definir interfaces comunes'
    ],
    advice: [
      'Practica creando clases abstractas',
      'Experimenta con diferentes métodos abstractos',
      'Aprende a diseñar interfaces comunes',
      'Practica implementando clases hijas'
    ]
  },
  {
    id: 30,
    title: 'Proyecto completo: Sistema de Biblioteca',
    description: 'Aplica todos los conceptos de POO creando un sistema completo de biblioteca con clases, herencia y métodos especiales.',
    initialCode: `# Define clases para el sistema de biblioteca
class Libro:
    def __init__(self, titulo, autor, isbn):
        self.titulo = titulo
        self.autor = autor
        self.isbn = isbn
        self.disponible = True
    
    def __str__(self):
        # Completa: devuelve "{titulo} por {autor}"
        pass
    
    def __eq__(self, otro):
        # Completa: compara por isbn
        pass

class Biblioteca:
    def __init__(self):
        self.libros = []
    
    def agregar_libro(self, libro):
        # Completa: agrega libro a self.libros
        pass
    
    def buscar_libro(self, isbn):
        # Completa: busca y devuelve el libro con ese isbn
        # Si no existe, devuelve None
        pass
    
    def prestar_libro(self, isbn):
        # Completa: marca el libro como no disponible
        # Si no existe o no está disponible, devuelve False
        pass

# Prueba el sistema
biblio = Biblioteca()
libro1 = Libro("Python 101", "Juan Pérez", "123")
libro2 = Libro("SQL Avanzado", "María García", "456")
biblio.agregar_libro(libro1)
biblio.agregar_libro(libro2)
print(f"Libro encontrado: {biblio.buscar_libro('123')}")
print(f"Préstamo exitoso: {biblio.prestar_libro('123')}")`,
    solution: `# Define clases para el sistema de biblioteca
class Libro:
    def __init__(self, titulo, autor, isbn):
        self.titulo = titulo
        self.autor = autor
        self.isbn = isbn
        self.disponible = True
    
    def __str__(self):
        # Completa: devuelve "{titulo} por {autor}"
        return f"{self.titulo} por {self.autor}"
    
    def __eq__(self, otro):
        # Completa: compara por isbn
        if not isinstance(otro, Libro):
            return False
        return self.isbn == otro.isbn

class Biblioteca:
    def __init__(self):
        self.libros = []
    
    def agregar_libro(self, libro):
        # Completa: agrega libro a self.libros
        self.libros.append(libro)
    
    def buscar_libro(self, isbn):
        # Completa: busca y devuelve el libro con ese isbn
        # Si no existe, devuelve None
        for libro in self.libros:
            if libro.isbn == isbn:
                return libro
        return None
    
    def prestar_libro(self, isbn):
        # Completa: marca el libro como no disponible
        # Si no existe o no está disponible, devuelve False
        libro = self.buscar_libro(isbn)
        if libro and libro.disponible:
            libro.disponible = False
            return True
        return False

# Prueba el sistema
biblio = Biblioteca()
libro1 = Libro("Python 101", "Juan Pérez", "123")
libro2 = Libro("SQL Avanzado", "María García", "456")
biblio.agregar_libro(libro1)
biblio.agregar_libro(libro2)
print(f"Libro encontrado: {biblio.buscar_libro('123')}")
print(f"Préstamo exitoso: {biblio.prestar_libro('123')}")`,
    testCases: [
      {
        id: 1,
        name: 'Test 1: Sistema completo de biblioteca',
        input: {},
        expectedOutput: ['Libro encontrado: Python 101 por Juan Pérez', 'Préstamo exitoso: True'],
        description: 'Verifica que el sistema completo funcione correctamente'
      }
    ],
    hints: [
      'Combina múltiples conceptos de POO',
      'Usa __str__ para representación legible',
      'Usa __eq__ para comparación',
      'Implementa lógica de negocio en métodos',
      'Este ejercicio integra todo lo aprendido'
    ],
    validateOnlyCode: false,
    why: {
      title: '¿Por qué un proyecto completo?',
      content: `Un **proyecto completo** integra todos los conceptos de POO aprendidos, demostrando cómo trabajar juntos en un sistema real.

### ¿Por qué es valioso?
- **Integración**: Combina todos los conceptos
- **Práctica**: Aplica conocimiento en contexto real
- **Comprensión**: Demuestra dominio completo
- **Portfolio**: Ejemplo de trabajo completo

### En el mundo real:
Este tipo de proyectos son exactamente lo que verás en el trabajo real: sistemas que combinan clases, herencia, métodos especiales y lógica de negocio.`,
      keyPoints: [
        'Integra todos los conceptos de POO',
        'Combina clases, métodos especiales y lógica',
        'Demuestra comprensión completa',
        'Ejemplo de sistema real'
      ],
      realWorldExample: 'Sistemas reales como gestión de inventario, sistemas de usuarios, o procesamiento de datos usan estos mismos conceptos integrados.'
    },
    theory: `# Proyecto Completo: Sistema de Biblioteca

## Integración de Conceptos

Este proyecto integra:
- **Clases**: Libro y Biblioteca
- **Métodos especiales**: __str__, __eq__
- **Lógica de negocio**: Prestar, buscar libros
- **Encapsulación**: Atributos protegidos
- **Métodos**: Operaciones del sistema

## Estructura

### Clase Libro
- Atributos: titulo, autor, isbn, disponible
- Métodos especiales: __str__, __eq__
- Representa un libro en el sistema

### Clase Biblioteca
- Atributos: libros (lista)
- Métodos: agregar_libro, buscar_libro, prestar_libro
- Gestiona la colección de libros

## Ventajas

- **Integración**: Combina todos los conceptos
- **Práctica**: Aplica conocimiento completo
- **Realismo**: Sistema similar a sistemas reales`,
    tips: [
      'Combina múltiples conceptos de POO',
      'Usa métodos especiales apropiados',
      'Implementa lógica de negocio clara',
      'Mantén el código organizado',
      'Este ejercicio integra todo lo aprendido'
    ],
    advice: [
      'Practica creando sistemas completos',
      'Experimenta integrando múltiples conceptos',
      'Aprende a diseñar sistemas orientados a objetos',
      'Practica con proyectos cada vez más complejos'
    ]
  }
];
